import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  makeStyles,
  Box,
  CircularProgress,
  Snackbar,
  Grid,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  useMediaQuery,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Drawer,
  AppBar,
  Toolbar,
  Avatar,
  Chip,
  Popper,
  Radio,
  FormControlLabel,
  Checkbox,
  Switch,
} from "@material-ui/core";
import { fade, lighten, darken } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Alert from "@material-ui/lab/Alert";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";
import EventIcon from "@material-ui/icons/Event";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ShoppingCartIcon from "@material-ui/icons/ShoppingCart";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import AiSensyLogo from "../../static/AiSensyLogo.svg";
import AiSensyLogoLight from "../../static/AiSensyLogo_light_bg.png";
import AiEditionEvolve from "../../static/evolve-ai-edition-logo.png";
import { useParams } from "react-router-dom";
import { eventApiService } from "../../services/eventApi";
import posthog from "../../config/posthog";
import PeopleIcon from "@material-ui/icons/People";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import WarningIcon from "@material-ui/icons/Warning";
import SearchIcon from "@material-ui/icons/Search";
import { countries } from "./countryData"; // We'll create this file in a moment

// Custom SVG icon components instead of using lucide-react
const MapPinIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"></path>
    <circle cx="12" cy="10" r="3"></circle>
  </svg>
);

const MessageSquareIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
  </svg>
);

const GlobeIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="2" x2="22" y1="12" y2="12"></line>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
  </svg>
);

const BriefcaseIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <rect width="20" height="14" x="2" y="7" rx="2" ry="2"></rect>
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
  </svg>
);

const CalendarIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <rect width="18" height="18" x="3" y="4" rx="2" ry="2"></rect>
    <line x1="16" x2="16" y1="2" y2="6"></line>
    <line x1="8" x2="8" y1="2" y2="6"></line>
    <line x1="3" x2="21" y1="10" y2="10"></line>
  </svg>
);

// Validation functions
const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const validatePhone = (phone) => {
  const phoneRegex = /^[0-9]+$/;
  return phoneRegex.test(phone.replace(/\D/g, ""));
};

const validateGstNumber = (gstNumber) => {
  // GST number format: 15 characters - 2 state code + 10 PAN + 1 entity code + 1 check digit + 1 default code
  const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
  return gstRegex.test(gstNumber.trim().toUpperCase());
};

const validateCompanyName = (name) => {
  return name && name.trim().length >= 2;
};

const validateCompanyAddress = (address) => {
  return address && address.trim().length >= 10;
};

// Organization size options
const organizationSizes = [
  "1-10 employees",
  "10-20 employees",
  "20-50 employees",
  "50-200 employees",
  "200+ employees",
];

// Industry options
const industries = [
  "Real Estate",
  "Technology",
  "Finance",
  "Healthcare",
  "Education",
  "E-commerce",
  "Manufacturing",
  "Professional Services",
  "Marketing",
  "Media",
  "Telecommunications",
  "Travel",
  "Government & Public Sector",
  "Non-Profit",
  "Others",
];

function AiSensyPaymentPage() {
  const theme = useTheme();
  const { eventSlug } = useParams();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [isVerifyingPayment, setIsVerifyingPayment] = useState(false);

  // Dark mode state
  const [isDarkMode, setIsDarkMode] = useState(() => false);

  // Dark mode toggle handler
  const handleDarkModeToggle = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    localStorage.setItem("darkMode", JSON.stringify(newDarkMode));
  };

  // Event config from backend
  const [eventConfig, setEventConfig] = useState(null);

  const primaryColor = eventConfig?.primaryColor || "#04CF66";
  const secondaryColor = eventConfig?.secondaryColor || "#000000";

  const classes = useStyles({
    isDarkMode,
    primaryColor,
    secondaryColor,
  });
  const [eventLoading, setEventLoading] = useState(true);
  const [showError, setShowError] = useState(false);
  const [summaryExpanded, setSummaryExpanded] = useState(false);

  // Add these global dropdown props
  const menuProps = {
    anchorOrigin: {
      vertical: "top",
      horizontal: "left",
    },
    transformOrigin: {
      vertical: "bottom",
      horizontal: "left",
    },
    getContentAnchorEl: null,
  };

  // Define custom popper for upward-opening dropdowns
  const CustomPopper = (props) => (
    <Popper
      {...props}
      placement="top-start"
      modifiers={{
        flip: {
          enabled: false,
        },
        preventOverflow: {
          enabled: true,
          boundariesElement: "window",
        },
      }}
    />
  );

  // Add these to the useState declarations
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    organizationName: "",
    organizationSize: "",
    industry: "",
  });

  // Order state
  const [couponCode, setCouponCode] = useState("");
  const [couponApplied, setCouponApplied] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [couponMessage, setCouponMessage] = useState("");
  const [currencySymbol, setCurrencySymbol] = useState("AED ");

  // Dynamic pricing data from backend
  const [pricingData, setPricingData] = useState(null);
  const [gstNumber, setGstNumber] = useState("");
  const [gstNumberError, setGstNumberError] = useState("");

  // GST Details state management
  const [showGstDetails, setShowGstDetails] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  const [companyNameError, setCompanyNameError] = useState("");
  const [companyAddressError, setCompanyAddressError] = useState("");

  const [validationErrors, setValidationErrors] = useState({
    fullName: "",
    email: "",
    phone: "",
    organizationName: "",
    organizationSize: "",
    industry: "",
  });
  const [couponData, setCouponData] = useState(null);

  // Track drawer state to prevent auto-expansion
  const [drawerWasExpanded, setDrawerWasExpanded] = useState(false);

  // Add states for urgency elements
  const [timeLeft, setTimeLeft] = useState({ days: 3, hours: 14, minutes: 22 });
  const [registrationCount, setRegistrationCount] = useState(142);
  const [availableSpots, setAvailableSpots] = useState({
    "Silver Pass - Single Member": 3,
    "Silver pass - Multi Member": 6,
    "Gold Pass - Single Member": 4,
    "Gold Pass - Multi Member": 3,
  });

  // Country selection with search functionality
  const [countrySearch, setCountrySearch] = useState("");
  const [selectedCountry, setSelectedCountry] = useState({
    code: "IN",
    dial_code: "+91",
    name: "India",
    flag: "🇮🇳",
  });

  // Add this at the end of the component return, just before the final closing div - remove the tooltip code if it exists
  const [quantityMsg, setQuantityMsg] = useState("");
  const [showQuantityMsg, setShowQuantityMsg] = useState(false);

  // Early Bird Timer (5 days from now)
  const [earlyBirdEndTime] = useState(() => {
    const fiveDaysFromNow = new Date();
    fiveDaysFromNow.setDate(fiveDaysFromNow.getDate() + 5);
    return fiveDaysFromNow;
  });

  const [earlyBirdTimeLeft, setEarlyBirdTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  const [eventStartTime, setEventStartTime] = useState(() => {
    // Default fallback date
    return new Date("2026-01-28T09:00:00+05:30");
  });

  // Brand logo from backend
  const [brandLogo, setBrandLogo] = useState(null);

  const [eventCountdown, setEventCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    hasStarted: false,
  });

  // Calculate pricing loading states
  const [calculatingPricing, setCalculatingPricing] = useState(false);
  const [pricingLoaded, setPricingLoaded] = useState(false);

  // Optimistic UI states for smooth interactions
  const [lastPricingRequest, setLastPricingRequest] = useState(null);

  // Load event config from backend
  useEffect(() => {
    // Track page load
    posthog.capture("payment_page_loaded", {
      event_slug: eventSlug,
      step: "user_details",
      timestamp: new Date().toISOString(),
    });

    const fetchEventConfig = async () => {
      if (!eventSlug) {
        setError("No event slug provided");
        setEventLoading(false);
        return;
      }

      try {
        setEventLoading(true);
        const response = await eventApiService.getEventConfig(eventSlug);

        if (response.success && response.data) {
          setEventConfig(response.data);

          // Update event data based on backend response
          setEventData({
            title: response.data.title || response.data.eventName,
            dateTime: response.data.eventDateInString || "Date TBD",
            location: response.data.venue
              ? `${response.data.venue}, ${response.data.location}`
              : response.data.location || "Location TBD",
          });

          // Update passes based on backend tickets
          if (response.data.tickets && response.data.tickets.length > 0) {
            const backendTickets = response.data.tickets.map((ticket) => ({
              id: ticket.id,
              title: ticket.title,
              subtitle: ticket.subtitle || "",
              description: ticket.description || "",
              earlyBirdPrice: ticket.earlyBirdPrice || null,
              isPopular: ticket.isPopular || false,
              isLimited: ticket.isLimited || false,
              price:
                ticket.earlyBirdPrice &&
                ticket.earlyBirdEndDate &&
                new Date() < new Date(ticket.earlyBirdEndDate)
                  ? ticket.earlyBirdPrice
                  : ticket.basePrice,
              originalPrice: ticket.basePrice,
              features: ticket.features || [],
              quantity: 0,
              maxQuantity: ticket.maxQuantity || 999,
              groupTicket: ticket.groupTicket || ticket.isGroup || false,
              minGroupSize: ticket.minGroupSize || 1,
              beforePrice: ticket.beforePrice || null,
              isSelectedByDefault: !!ticket.isSelectedByDefault,
            }));

            const initialQuantities = {};
            backendTickets.forEach((ticket) => {
              let qty = 0;
              if (ticket.isSelectedByDefault) {
                qty = ticket.groupTicket ? ticket.minGroupSize || 1 : 1;
              }
              initialQuantities[ticket.id] = qty;
            });

            const ticketsWithQuantities = backendTickets.map((t) => ({
              ...t,
              quantity: initialQuantities[t.id] || 0,
            }));

            setPasses(ticketsWithQuantities);
            setQuantities(initialQuantities);
          }

          // Update country based on event location
          if (response.data.country) {
            const countryCode = getCountryCode(response.data.country);
            const country = countries.find((c) => c.code === countryCode);
            if (country) {
              setSelectedCountry(country);
            }
          }

          if (response.data.isDarkMode) {
            setIsDarkMode(true);
          }

          // Update document title
          document.title =
            response.data.title ||
            response.data.eventName ||
            "AiSensy Checkout";

          // Update currency symbol
          setCurrencySymbol(response.data.currencySymbol || "AED ");

          // Update event start time from backend config
          if (response.data.eventDate) {
            setEventStartTime(new Date(response.data.eventDate));
          }

          // Update brand logo from backend config
          if (response.data.brandLogo) {
            setBrandLogo(response.data.brandLogo);
          }
        } else {
          throw new Error("Event not found");
        }
      } catch (err) {
        console.error("Failed to load event config:", err);

        // Handle different types of config loading errors
        if (err.data && err.data.type === "VALIDATION_ERROR") {
          setError(err.data.message || "Invalid event configuration");
        } else if (err.status === 404) {
          setError("Event not found. Please check the URL and try again.");
        } else if (err.status === 0) {
          setError(
            "Unable to connect to server. Please check your internet connection."
          );
        } else {
          setError(
            err.message ||
              err.data?.message ||
              "Failed to load event details. Please try again."
          );
        }
        setShowError(true);
      } finally {
        setEventLoading(false);
      }
    };

    fetchEventConfig();
  }, [eventSlug]);

  // Early Bird Timer Effect
  useEffect(() => {
    const updateEarlyBirdTimer = () => {
      const now = new Date().getTime();
      const distance = earlyBirdEndTime.getTime() - now;

      if (distance > 0) {
        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor(
          (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        );
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);

        setEarlyBirdTimeLeft({ days, hours, minutes, seconds });
      } else {
        setEarlyBirdTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    updateEarlyBirdTimer();
    const timer = setInterval(updateEarlyBirdTimer, 1000);

    return () => clearInterval(timer);
  }, [earlyBirdEndTime]);

  useEffect(() => {
    const updateEventCountdown = () => {
      const now = new Date().getTime();
      const distance = eventStartTime.getTime() - now;

      if (distance > 0) {
        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor(
          (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        );
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);

        setEventCountdown({ days, hours, minutes, seconds, hasStarted: false });
      } else {
        setEventCountdown({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
          hasStarted: true,
        });
      }
    };

    updateEventCountdown();
    const timer = setInterval(updateEventCountdown, 1000);

    return () => clearInterval(timer);
  }, [eventStartTime]);

  // Helper function to get country code from country name
  const getCountryCode = (countryName) => {
    const countryMap = {
      India: "IN",
      "United Arab Emirates": "AE",
      UAE: "AE",
      Dubai: "AE",
      Singapore: "SG",
      "United States": "US",
      USA: "US",
      "United Kingdom": "GB",
      UK: "GB",
    };
    return countryMap[countryName] || "IN";
  };

  // Filter countries based on search (same as Regional Plans page)
  const filteredCountries = countries.filter(
    (country) =>
      country.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
      country.dial_code.includes(countrySearch)
  );

  // Helper function to scroll to error field
  const scrollToErrorField = (fieldName) => {
    console.log("Scrolling to error field:", fieldName);

    // Map field names to actual input IDs/names
    const fieldMap = {
      fullName: "fullName",
      email: "email",
      phone: "phone",
      organizationName: "organizationName",
      organizationSize: "organizationSize",
      industry: "industry",
    };

    const targetField = fieldMap[fieldName];
    if (targetField) {
      // Try to find the input field
      const inputElement = document.querySelector(
        `input[name="${targetField}"], select[name="${targetField}"]`
      );

      if (inputElement) {
        // Smooth scroll to the field
        inputElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });

        // Focus the field after scrolling
        setTimeout(() => {
          inputElement.focus();
        }, 500);
      }
    }
  };

  // Helper function to highlight error field with visual feedback
  const highlightErrorField = (fieldName) => {
    console.log("Highlighting error field:", fieldName);

    const fieldMap = {
      fullName: "fullName",
      email: "email",
      phone: "phone",
      organizationName: "organizationName",
      organizationSize: "organizationSize",
      industry: "industry",
    };

    const targetField = fieldMap[fieldName];
    if (targetField) {
      const inputElement = document.querySelector(
        `input[name="${targetField}"], select[name="${targetField}"]`
      );

      if (inputElement) {
        // Add highlight class for visual feedback
        inputElement.style.animation = "shake 0.5s ease-in-out";
        inputElement.style.boxShadow = "0 0 0 3px rgba(244, 67, 54, 0.3)";
        inputElement.style.borderColor = "#f44336";

        // Remove highlight after animation
        setTimeout(() => {
          inputElement.style.animation = "";
          inputElement.style.boxShadow = "";
          inputElement.style.borderColor = "";
        }, 2000);
      }
    }
  };

  // Razorpay payment modal integration with backend verification
  const openRazorpayModal = (paymentData) => {
    if (!window.Razorpay) {
      console.error("Razorpay SDK not loaded");
      setError("Payment system not available. Please try again.");
      setShowError(true);
      return;
    }

    // Track Razorpay modal opening
    posthog.capture("razorpay_modal_opened", {
      event_slug: eventSlug,
      amount: paymentData.amount,
      currency: paymentData.currency || "INR",
    });

    const options = {
      key: paymentData.public_key,
      amount: paymentData.amount * 100, // Amount in paise (backend sends in rupees)
      currency: "INR",
      name: "AISENSY COMMUNICATIONS PVT LTD",
      description:
        paymentData.description || `${eventConfig?.eventName} Registration`,
      order_id: paymentData.razorpayOrderId,
      retry: { enabled: true },
      remember_customer: true,
      handler: async (response) => {
        try {
          setSubmitting(true);
          setIsVerifyingPayment(true);

          // Verify payment with backend using correct structure
          const verificationData = {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            customerId: paymentData.customerId,
          };

          const verifyResponse = await eventApiService.verifyPayment(
            eventSlug,
            verificationData
          );

          if (verifyResponse.success && verifyResponse.data) {
            // Backend returns: { customerId, status }
            console.log("Payment verified successfully:", verifyResponse.data);

            // Redirect to thank you page with proper parameters
            const thankYouUrl = `/${eventSlug}/thankyou?customerId=${verifyResponse.data.customerId}&orderId=${response.razorpay_order_id}`;
            window.location.href = thankYouUrl;
          } else {
            throw new Error(
              verifyResponse.message || "Payment verification failed"
            );
          }
        } catch (error) {
          // Handle payment verification errors with proper context
          let errorMessage = "Payment verification failed. ";

          if (error.data && error.data.type === "VALIDATION_ERROR") {
            errorMessage += error.data.message || "Invalid payment data.";
          } else if (error.status === 400) {
            errorMessage += "Invalid payment signature.";
          } else if (error.status === 404) {
            errorMessage += "Payment record not found.";
          } else {
            errorMessage += error.message || "Unknown error occurred.";
          }

          errorMessage += ` Please contact support with your payment ID: ${response.razorpay_payment_id}`;

          setError(errorMessage);
          setShowError(true);
        } finally {
          setSubmitting(false);
          setIsVerifyingPayment(false);
        }
      },
      prefill: {
        name: formData.fullName,
        email: formData.email,
        contact: formData.phone.replace(/\D/g, ""), // Only digits for Razorpay
      },
      theme: {
        color: primaryColor,
      },
      modal: {
        backdropclose: false,
        escape: false,
        handleback: true,
        animation: true,
        confirm_close: true,
        ondismiss: () => {
          setSubmitting(false);
          console.log("Payment modal dismissed by user");
        },
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.on("payment.failed", (response) => {
      setError(
        `Payment failed: ${
          response.error.description || response.error.reason || "Unknown error"
        }. Please try again or contact support.`
      );
      setShowError(true);
      setSubmitting(false);
    });

    rzp.open();
  };

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => {
      console.log("Razorpay script loaded successfully");
    };
    script.onerror = () => {
      console.error("Failed to load Razorpay script");
    };
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  useEffect(() => {
    const script = document.createElement("script");
    script.src =
      "https://cdn.jsdelivr.net/npm/canvas-confetti@1.9.2/dist/confetti.browser.min.js";
    script.async = true;
    script.onload = () => {
      console.log("Confetti script loaded successfully");
    };
    script.onerror = () => {
      console.error("Failed to load Confetti script");
    };
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search || "");
    if (params.get("view") === "tickets") {
      params.delete("view");
      const newSearch = params.toString();
      const newUrl = `${window.location.pathname}${
        newSearch ? `?${newSearch}` : ""
      }`;
      try {
        window.history.replaceState({}, "", newUrl);
      } catch (e) {}
      if (activeStep !== 0) setActiveStep(0);
    }
  }, [eventSlug]);

  // Event details (will be populated from backend)
  const [eventData, setEventData] = useState({
    title: "Loading...",
    dateTime: "Loading...",
    location: "Loading...",
  });

  // Passes/tickets will be loaded from backend event config
  const [passes, setPasses] = useState([]);

  // Track quantities separately for optimistic updates
  const [quantities, setQuantities] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear validation error when user types
    setValidationErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    // Identify user when email is entered
    if (name === "email" && value && value.includes("@")) {
      posthog.identify(value, {
        email: value,
        event_slug: eventSlug,
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    let isValid = true;

    if (!formData.fullName.trim()) {
      errors.fullName = "Full name is required";
      isValid = false;
    }

    if (!formData.email) {
      errors.email = "Email is required";
      isValid = false;
    }

    if (!validateEmail(formData.email)) {
      errors.email = "Please enter a valid email address";
      isValid = false;
    }

    if (!formData.phone) {
      errors.phone = "Phone number is required";
      isValid = false;
    } else if (!validatePhone(formData.phone)) {
      errors.phone = "Please enter a valid phone number";
      isValid = false;
    } else if (selectedCountry.code === "IN" && formData.phone.length !== 10) {
      errors.phone = "Phone number must be 10 digits";
      isValid = false;
    }

    if (!formData.organizationName.trim()) {
      errors.organizationName = "Organization name is required";
      isValid = false;
    }

    if (!formData.organizationSize) {
      errors.organizationSize = "Organization size is required";
      isValid = false;
    }

    setValidationErrors(errors);
    return isValid;
  };

  const handleCloseError = () => {
    setShowError(false);
  };

  const handleCloseQuantityMsg = () => {
    setShowQuantityMsg(false);
  };

  const handleCouponChange = (e) => {
    const upperCaseValue = e.target.value.toUpperCase();
    setCouponCode(upperCaseValue);

    // Clear coupon message and state whenever user changes input
    if (couponMessage) {
      setCouponMessage("");
    }
    if (couponApplied) {
      setCouponApplied(false);
      setDiscount(0);
      setCouponData(null);
    }
  };

  // Handle GST number input with validation
  const handleGstNumberChange = (event) => {
    const value = event.target.value.toUpperCase().trim();
    setGstNumber(value);

    // Clear error when user starts typing
    if (gstNumberError) {
      setGstNumberError("");
    }

    // Validate GST number format if not empty
    if (value && !validateGstNumber(value)) {
      setGstNumberError("Please enter a valid GST number");
    }
  };

  // Handle GST details checkbox toggle
  const handleGstDetailsToggle = (event) => {
    const checked = event.target.checked;
    setShowGstDetails(checked);

    // Clear all GST related fields when unchecked
    if (!checked) {
      setGstNumber("");
      setCompanyName("");
      setCompanyAddress("");
      setGstNumberError("");
      setCompanyNameError("");
      setCompanyAddressError("");
    }
  };

  // Handle company name input with validation
  const handleCompanyNameChange = (event) => {
    const value = event.target.value.toUpperCase();
    setCompanyName(value);

    // Clear error when user starts typing
    if (companyNameError) {
      setCompanyNameError("");
    }

    // Validate company name if GST number is provided
    if (gstNumber && value && !validateCompanyName(value)) {
      setCompanyNameError("");
    }
  };

  // Handle company address input with validation
  const handleCompanyAddressChange = (event) => {
    const value = event.target.value;
    setCompanyAddress(value);

    // Clear error when user starts typing
    if (companyAddressError) {
      setCompanyAddressError("");
    }

    // Validate company address if GST number is provided
    if (gstNumber && value && !validateCompanyAddress(value)) {
      setCompanyAddressError("Company address must be at least 10 characters");
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponMessage("Please enter a coupon code");
      return;
    }

    setValidatingCoupon(true);
    setCouponMessage("");

    // Track coupon application attempt
    posthog.capture("coupon_application_attempted", {
      coupon_code: couponCode,
      event_slug: eventSlug,
    });

    try {
      // Use calculate pricing API to validate coupon
      await calculatePricingFromBackend(true); // Force calculation with coupon
    } catch (err) {
      console.error("Coupon verification error:", err);
      setCouponApplied(false);
      setDiscount(0);
      setCouponData(null);
      setCouponMessage("Failed to verify coupon");
    } finally {
      setValidatingCoupon(false);
    }
  };

  // Confetti function
  const triggerConfetti = () => {
    const count = 200;
    const defaults = {
      origin: { y: 0.7 },
      zIndex: 10000,
    };

    function fire(particleRatio, opts) {
      const confetti = window.confetti;
      if (confetti) {
        confetti({
          ...defaults,
          ...opts,
          particleCount: Math.floor(count * particleRatio),
          colors: [
            primaryColor,
            primaryColor,
            lighten(primaryColor, 0.2),
            "#ffffff",
          ],
        });
      }
    }

    fire(0.25, {
      spread: 26,
      startVelocity: 55,
    });
    fire(0.2, {
      spread: 60,
    });
    fire(0.35, {
      spread: 100,
      decay: 0.91,
      scalar: 0.8,
    });
    fire(0.1, {
      spread: 120,
      startVelocity: 25,
      decay: 0.92,
      scalar: 1.2,
    });
    fire(0.1, {
      spread: 120,
      startVelocity: 45,
    });
  };

  const handleQuantityChange = (passId, change, passTitle = "") => {
    // Get the pass to modify
    const passToModify = passes.find(
      (pass) =>
        pass.id === passId && (passTitle === "" || pass.title === passTitle)
    );

    if (!passToModify) return;

    const currentQuantity = quantities[passId] || passToModify.quantity || 0;
    let newQuantity = currentQuantity + change;

    // Apply group ticket logic using pass config (from backend)
    if (passToModify.groupTicket) {
      const minGroupSize = passToModify.minGroupSize || 2;

      if (currentQuantity === 0 && change > 0) {
        // Adding group ticket starts at minimum group size
        newQuantity = minGroupSize;
      } else if (newQuantity > 0 && newQuantity < minGroupSize) {
        // If reducing below minimum, set to 0 (remove ticket)
        newQuantity = 0;
      }
    }

    // Ensure quantity is within bounds
    newQuantity = Math.max(
      0,
      Math.min(newQuantity, passToModify.maxQuantity || 999)
    );

    // Track if all quantities are becoming 0 (drawer should close)
    const allQuantitiesWillBeZero =
      newQuantity === 0 &&
      Object.values({ ...quantities, [passId]: newQuantity }).every(
        (qty) => qty === 0
      );

    if (allQuantitiesWillBeZero) {
      // Close drawer
      setSummaryExpanded(false);
      setDrawerWasExpanded(false);

      // Reset coupon states to default
      setCouponApplied(false);
      setCouponCode("");
      setCouponMessage("");
      setCouponData(null);
      setDiscount(0);

      // Reset GST states to default
      setGstNumber("");
      setGstNumberError("");

      // Reset pricing states
      setPricingData(null);
      setPricingLoaded(false);
    }

    // Update quantities state for immediate UI response
    setQuantities((prev) => ({
      ...prev,
      [passId]: newQuantity,
    }));

    // Also update passes state for consistency
    setPasses((prevPasses) =>
      prevPasses.map((pass) =>
        pass.id === passId && (passTitle === "" || pass.title === passTitle)
          ? { ...pass, quantity: newQuantity }
          : pass
      )
    );

    console.log(
      `🎫 ${passToModify.title}: ${currentQuantity} → ${newQuantity} (change: ${change})`
    );

    // Track pass quantity changes
    posthog.capture("pass_quantity_changed", {
      pass_id: passId,
      pass_title: passToModify.title,
      change: change,
      new_quantity: newQuantity,
      event_slug: eventSlug,
    });
  };

  // Helper function to calculate subtotal from a passes array
  const getSubtotalFromPasses = (passesArray) => {
    return parseFloat(
      passesArray
        .reduce((total, pass) => total + (pass.price * pass.quantity || 0), 0)
        .toFixed(2)
    );
  };

  // Helper functions - ONLY use backend pricing data
  const getSubtotal = () => {
    return pricingData?.subtotal || 0;
  };

  const getTotalAmount = () => {
    return pricingData?.finalAmount || 0;
  };

  const getGstAmount = () => {
    return pricingData?.gstDetails?.amount || 0;
  };

  const getDiscountAmount = () => {
    return pricingData?.discount || 0;
  };

  const getCouponDisplayText = () => {
    if (!pricingData?.couponDetails?.code) return "";

    const { code, type, value } = pricingData.couponDetails;

    if (type === "percentage") {
      return ` (${value}% OFF)`;
    } else if (type === "fixed") {
      return ` (${currencySymbol}${value} OFF)`;
    } else {
      return ` (${code})`;
    }
  };

  const hasGst = () => {
    return pricingData?.gstDetails?.isApplicable || false;
  };

  // Helper to get current quantities from state
  const getCurrentQuantity = (passId) => {
    return quantities[passId] || 0;
  };

  // Calculate pricing using backend API with correct structure
  const calculatePricingFromBackend = async (forceCouponValidation = false) => {
    if (!eventConfig || !eventSlug) {
      setPricingLoaded(false);
      return;
    }

    // Calculate selected tickets first
    const selectedTickets = passes
      .filter((pass) => getCurrentQuantity(pass.id) > 0)
      .map((pass) => ({
        id: pass.id,
        quantity: getCurrentQuantity(pass.id),
      }));

    // Include coupon in request if we're forcing validation or if already applied
    const shouldIncludeCoupon = forceCouponValidation || couponApplied;
    const couponToSend =
      shouldIncludeCoupon && couponCode.trim() ? couponCode : undefined;

    // Create request signature for caching and duplicate prevention (no GST)
    const currentRequest = JSON.stringify({
      tickets: selectedTickets,
      coupon: couponToSend,
    });

    // Prevent multiple simultaneous calculations for the same request
    if (
      calculatingPricing &&
      lastPricingRequest === currentRequest &&
      !forceCouponValidation
    )
      return;

    setLastPricingRequest(currentRequest);

    // Set calculating flag but don't immediately set pricing loaded to false
    // This prevents the flicker effect
    setCalculatingPricing(true);

    if (selectedTickets.length === 0) {
      setPricingData(null);
      setPricingLoaded(false);
      setCalculatingPricing(false);
      return;
    }

    // Set loading state but don't block UI interactions
    setPricingLoaded(false);

    try {
      // Backend expects: { tickets: [{ id, quantity }], couponCode? } - No GST for pricing
      const pricingRequest = {
        tickets: selectedTickets,
        couponCode: couponToSend,
      };

      const response = await eventApiService.calculatePricing(
        eventSlug,
        pricingRequest
      );

      if (response.success && response.data) {
        setPricingData(response.data);
        setPricingLoaded(true);
        setDiscount(response.data.discount || 0);

        // Update coupon state based on response
        if (response.data.couponDetails && couponToSend) {
          const wasNotApplied = !couponApplied;
          setCouponApplied(true);
          setCouponData(response.data.couponDetails);
          setCouponMessage(`Coupon applied successfully!`);

          // Trigger confetti only on new coupon application
          if (wasNotApplied && forceCouponValidation) {
            setTimeout(() => triggerConfetti(), 100);
          }
        } else if (
          forceCouponValidation &&
          couponToSend &&
          !response.data.couponDetails
        ) {
          // Coupon was attempted but not applied
          setCouponApplied(false);
          setCouponData(null);
          setCouponMessage("Invalid or expired coupon code");
        } else if (!couponToSend) {
          // No coupon in request, reset coupon state
          setCouponApplied(false);
          setCouponData(null);
          setCouponMessage("");
        }
      } else {
        throw new Error(response.message || "Failed to calculate pricing");
      }
    } catch (error) {
      console.error("Failed to calculate pricing:", error);
      setPricingData(null);
      setPricingLoaded(false);

      // Handle different types of pricing errors
      if (error.data && error.data.type === "VALIDATION_ERROR") {
        setError(error.data.message || "Invalid pricing data");
        setShowError(true);
      } else if (forceCouponValidation && couponToSend) {
        setCouponApplied(false);
        setCouponData(null);
        setCouponMessage(
          error.message || error.data?.message || "Invalid coupon code"
        );
      }
    } finally {
      // Small delay to prevent flicker on very fast API responses
      setTimeout(() => {
        setCalculatingPricing(false);
      }, 100);
    }
  };

  // Recalculate pricing when quantities or GST details change (not coupon)
  useEffect(() => {
    const hasSelectedItems = Object.values(quantities).some((qty) => qty > 0);

    // Only calculate if there are items selected
    if (!hasSelectedItems) {
      setPricingData(null);
      setPricingLoaded(false);
      setCalculatingPricing(false);
      return;
    }

    const timer = setTimeout(() => {
      calculatePricingFromBackend();
    }, 200); // Even faster debounce for butter-smooth experience

    return () => clearTimeout(timer);
  }, [
    quantities, // Use quantities state instead of passes
    eventConfig,
  ]);

  // Calculate amounts (updated to use dynamic pricing)
  const getSubtotalStatic = () => {
    return parseFloat(
      passes
        .reduce((total, pass) => total + (pass.price * pass.quantity || 0), 0)
        .toFixed(2)
    );
  };

  // Check if cart has any items
  const hasItems = () => {
    return passes.some((pass) => getCurrentQuantity(pass.id) > 0);
  };

  // Add Meta Pixel tracking code // Empty dependency array means this effect runs once on mount

  // Submit form to backend API with proper error handling
  const sendFormDataToBackend = async (formDataWithCode) => {
    try {
      if (eventSlug) {
        // Backend expects: fullName, email, phone, countryCallingCode, isoCode, organizationName, organizationSize, industry
        const backendFormData = {
          fullName: formDataWithCode.fullName,
          email: formDataWithCode.email,
          phone: formDataWithCode.phone.replace(/\D/g, ""),
          countryCallingCode: selectedCountry.dial_code,
          isoCode: selectedCountry.code,
          organizationName: formDataWithCode.organizationName || undefined,
          organizationSize: formDataWithCode.organizationSize || undefined,
          industry: formDataWithCode.industry || undefined,
          source: "checkout-page",
        };

        const response = await eventApiService.submitForm(
          eventSlug,
          backendFormData
        );

        if (response.success) {
          console.log("Form submitted successfully:", response.data);
          // Store customer ID for later use
          window.localStorage.setItem("customerId", response.data.customerId);
          return response.data;
        } else {
          // Handle non-success response
          const errorMsg =
            response && response.message
              ? String(response.message)
              : "Form submission failed";
          throw new Error(errorMsg);
        }
      }
    } catch (error) {
      console.error("Error sending form data to backend:", error);
      console.log("Error object:", JSON.stringify(error, null, 2)); // Debug log

      let errorMessage = "Failed to submit form. Please try again.";

      // Handle backend validation errors
      if (
        error &&
        error.data &&
        error.data.type === "VALIDATION_ERROR" &&
        error.data.errors
      ) {
        console.log("Handling validation error:", error.data);

        // Clear previous validation errors
        setValidationErrors({
          fullName: "",
          email: "",
          phone: "",
          organizationName: "",
          organizationSize: "",
          industry: "",
        });

        // Set field-specific errors
        const newValidationErrors = {};
        let firstErrorField = null;

        error.data.errors.forEach((err) => {
          if (err && err.field && err.message) {
            newValidationErrors[err.field] = String(err.message);

            // Store the first error field for scrolling/highlighting
            if (!firstErrorField) {
              firstErrorField = err.field;
            }
          }
        });

        setValidationErrors((prev) => ({ ...prev, ...newValidationErrors }));

        // Scroll to and highlight the first error field
        if (firstErrorField) {
          setTimeout(() => {
            scrollToErrorField(firstErrorField);
            highlightErrorField(firstErrorField);
          }, 100); // Small delay to ensure state updates
        }

        // Show general error message in snackbar
        errorMessage = String(
          error.data.message || "Please fix the form errors and try again"
        );
      } else if (error && error.message) {
        // Handle other errors with message
        errorMessage = String(error.message);
      } else if (error && error.data && error.data.message) {
        // Handle errors with data.message
        errorMessage = String(error.data.message);
      } else if (typeof error === "string") {
        // Handle string errors
        errorMessage = error;
      } else {
        // Fallback for unknown error types
        console.warn("Unknown error type:", typeof error, error);
        errorMessage = "An unexpected error occurred. Please try again.";
      }

      // Ensure errorMessage is always a string
      if (typeof errorMessage !== "string") {
        console.warn(
          "Error message is not a string:",
          typeof errorMessage,
          errorMessage
        );
        errorMessage = "An error occurred. Please try again.";
      }

      console.log("Setting error message:", errorMessage);
      setError(errorMessage);
      setShowError(true);

      // Re-throw error to prevent form progression
      throw new Error(errorMessage);
    }
  };

  const handleProceed = async () => {
    if (activeStep === 0) {
      // First step - validate form before proceeding
      if (!validateForm()) {
        return;
      }

      const formDataWithCountryCode = {
        ...formData,
        phone: `${selectedCountry.dial_code}${formData.phone}`,
      };

      try {
        setSubmitting(true);
        setError("");
        setShowError(false);

        await sendFormDataToBackend(formDataWithCountryCode);

        if (window.fbq) {
          window.fbq("track", "Lead", {
            content_name: eventData.title,
            content_category: "Event Registration",
          });
        }

        setActiveStep(1);

        // Track step progression
        posthog.capture("payment_step_proceeded", {
          from_step: "user_details",
          to_step: "passes",
          event_slug: eventSlug,
          user_email: formData.email,
        });

        try {
          const url = new URL(window.location.href);
          url.searchParams.set("view", "tickets");
          window.history.replaceState(
            {},
            "",
            `${url.pathname}?${url.searchParams.toString()}`
          );
        } catch (e) {}

        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      } catch (error) {
        return;
      } finally {
        setSubmitting(false);
      }
    } else {
      const totalItems = passes.reduce(
        (sum, pass) => sum + getCurrentQuantity(pass.id),
        0
      );
      if (totalItems === 0) {
        setError("Please select at least one pass to continue");
        setShowError(true);
        return;
      }
      handleSubmit();
    }
  };

  const handleBack = () => {
    setActiveStep(0);
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("view");
      const newSearch = url.searchParams.toString();
      window.history.replaceState(
        {},
        "",
        `${url.pathname}${newSearch ? `?${newSearch}` : ""}`
      );
    } catch (e) {}
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    const totalItems = passes.reduce(
      (sum, pass) => sum + getCurrentQuantity(pass.id),
      0
    );
    const cartTotal = getSubtotal();

    if (totalItems === 0) {
      setError("Please select at least one pass to continue");
      setShowError(true);
      return;
    }

    const selectedPasses = passes.filter(
      (pass) => getCurrentQuantity(pass.id) > 0
    );
    const orderDetails = selectedPasses.map((pass) => ({
      id: pass.id,
      title: pass.title,
      price: pass.price,
      quantity: getCurrentQuantity(pass.id),
    }));

    setSubmitting(true);

    // Track payment initiation
    posthog.capture("payment_initiated", {
      event_slug: eventSlug,
      total_amount: getTotalAmount(),
      subtotal: getSubtotal(),
      gst_amount: getGstAmount(),
      discount_amount: getDiscountAmount(),
      user_email: formData.email,
      selected_passes: orderDetails,
    });

    try {
      const paymentFormData = {
        ...formData,
        phone: formData.phone.startsWith("+")
          ? formData.phone
          : `${selectedCountry.dial_code}${formData.phone}`,
      };

      const paymentData = {
        ...paymentFormData,
        passes: orderDetails,
        couponCode: couponApplied ? couponCode : "",
      };

      if (eventSlug) {
        const selectedTickets = passes
          .filter((pass) => getCurrentQuantity(pass.id) > 0)
          .map((pass) => ({
            id: pass.id,
            quantity: getCurrentQuantity(pass.id),
          }));

        // Validate GST details if provided
        if (showGstDetails) {
          if (!gstNumber || !companyName || !companyAddress) {
            setError("Please enter all the GST details before proceeding.");
            setShowError(true);
            return;
          }

          if (gstNumber && !validateGstNumber(gstNumber)) {
            setError("Please enter a valid GST number before proceeding.");
            setShowError(true);
            return;
          }
          if (gstNumber && companyName && !validateCompanyName(companyName)) {
            setError("Please enter a valid company name.");
            setShowError(true);
            return;
          }
          if (
            gstNumber &&
            companyAddress &&
            !validateCompanyAddress(companyAddress)
          ) {
            setError("Please enter a valid company address.");
            setShowError(true);
            return;
          }
        }

        // Build GST details object
        const hasValidGstDetails =
          showGstDetails && gstNumber && validateGstNumber(gstNumber);
        const gstDetailsObj = hasValidGstDetails
          ? {
              gstNumber: gstNumber,
              ...(companyName && { companyName: companyName.trim() }),
              ...(companyAddress && { companyAddress: companyAddress.trim() }),
            }
          : undefined;

        const paymentIntentRequest = {
          email: formData.email,
          tickets: selectedTickets,
          couponCode: couponApplied && couponCode ? couponCode : undefined,
          gstDetails: gstDetailsObj,
          intentData: {
            redirectUrl: `${window.location.origin}/${eventSlug}/thankyou`,
            cancelUrl: `${window.location.origin}/${eventSlug}`,
          },
        };

        const response = await eventApiService.createPaymentIntent(
          eventSlug,
          paymentIntentRequest
        );

        if (response.success && response.data) {
          if (response.data.razorpayOrderId && response.data.public_key) {
            openRazorpayModal(response.data);
          } else if (response.data.paymentUrl) {
            window.location.href = response.data.paymentUrl;
          } else {
            throw new Error("Payment configuration not received from backend");
          }
        } else {
          throw new Error(response.message || "Payment intent creation failed");
        }
      } else {
        throw new Error("Event slug is required for payment");
      }
    } catch (error) {
      console.error("Payment intent creation failed:", error);

      if (
        error.data &&
        error.data.type === "VALIDATION_ERROR" &&
        error.data.errors
      ) {
        let errorMessage = error.data.message || "Payment validation failed";

        if (error.data.errors.length > 0) {
          const fieldErrors = error.data.errors
            .map((err) => `${err.field}: ${err.message}`)
            .join(", ");
          errorMessage += ` (${fieldErrors})`;
        }

        setError(errorMessage);
      } else {
        setError(
          error.message ||
            error.data?.message ||
            error.response?.data?.message ||
            "Failed to create payment intent. Please try again."
        );
      }
      setShowError(true);
    } finally {
      setSubmitting(false);
    }
  };

  const renderHeader = () => {
    // Determine which logo to use: backend brandLogo > default based on theme
    const logoSrc = brandLogo || (isDarkMode ? AiEditionEvolve : AiSensyLogo);

    return (
      <>
        <Box className={classes.headerLogo}>
          <img src={logoSrc} alt="Event Logo" className={classes.logo} />
        </Box>

        {/* <Typography variant="h5" className={classes.eventTitle}>
          EVOLVE<span className={classes.greenText}>'25</span> : AI Edition
        </Typography> */}

        <Box className={classes.eventBadges}>
          <Chip
            icon={
              <BriefcaseIcon
                className={classes.customIcon}
                style={{ marginRight: "-4px" }}
              />
            }
            label="Business"
            className={classes.dubaiChip}
            size="small"
          />
          <Chip
            icon={
              <MessageSquareIcon
                className={classes.customIcon}
                style={{ marginRight: "-4px" }}
              />
            }
            label="WhatsApp"
            className={classes.whatsappChip}
            size="small"
          />
          <Chip
            icon={
              <GlobeIcon
                className={classes.customIcon}
                style={{ marginRight: "-4px" }}
              />
            }
            label="AI"
            className={classes.aiChip}
            size="small"
          />
          {/* <Chip
              icon={
                <BriefcaseIcon
                  className={classes.customIcon}
                  style={{ marginRight: "-4px" }}
                />
              }
              label="Business"
              className={classes.businessChip}
              size="small"
          /> */}
        </Box>

        <Typography variant="body2" className={classes.eventDate}>
          <MapPinIcon className={classes.customIcon} />
          <span className={classes.eventDateText}>{eventData.location}</span>
        </Typography>

        {activeStep === 1 && <Box mt={0.1}></Box>}

        <Typography variant="body2" className={classes.eventDate}>
          <CalendarIcon className={classes.customIcon} />
          <span className={classes.eventDateText}>{eventData.dateTime}</span>
        </Typography>
      </>
    );
  };

  const renderUserDetailsStep = () => {
    return (
      <>
        {/* Sticky Countdown Banner at Top */}
        <Box className={classes.stickyCountdownBanner}>
          <Typography className={classes.countdownText}>
            <AccessTimeIcon fontSize="small" style={{ fontSize: 20 }} />
            {!eventCountdown.hasStarted
              ? `Event starts in: ${eventCountdown.days}d ${eventCountdown.hours}h ${eventCountdown.minutes}m ${eventCountdown.seconds}s`
              : "🎉 Event has started! Secure your spot now!"}
          </Typography>
        </Box>

        <Container
          maxWidth="lg"
          className={isMobile ? classes.mobileFormContainer : ""}
          style={{ marginTop: "60px" }}
        >
          {/* Add floating countdown for mobile */}
          {/* {isMobile && (
            <Box className={classes.floatingBadge}>
              <AccessTimeIcon fontSize="small" style={{ marginRight: "4px" }} />
              Limited offer ends soon!
            </Box>
          )} */}

          {/* Display side by side on desktop, stacked on mobile */}
          <Grid container spacing={3}>
            {/* Header section - full width on mobile, left side on desktop */}
            <Grid item xs={12} md={5}>
              {renderHeader()}

              {/* Add urgency badge with real countdown */}
              {/* {isMobile && (
              <Box display="flex" justifyContent="center" mt={2}>
                <Box className={classes.urgencyBadge}>
                  <span className={classes.pulsingDot}></span>
                  {timeLeft.days > 0 ? (
                    <Typography
                      variant="body2"
                      style={{ fontSize: 13, fontWeight: 600 }}
                    >
                      Final Countdown: {timeLeft.days}d {timeLeft.hours}h{" "}
                      {timeLeft.minutes}m
                    </Typography>
                  ) : (
                    <Typography
                      variant="body2"
                      style={{ fontSize: 13, fontWeight: 600 }}
                    >
                      LAST DAY TO REGISTER! Only {timeLeft.hours}h{" "}
                      {timeLeft.minutes}m left
                    </Typography>
                  )}
                </Box>
              </Box>
            )} */}
            </Grid>

            {/* Form section - full width on mobile, right side on desktop */}
            <Grid item xs={12} md={7}>
              <Box display="flex" alignItems="center">
                <Typography variant="h5" className={classes.formTitle}>
                  Get Your Tickets Now!
                </Typography>
                {/* <Box className={classes.limitedSpotsBadge}>
                <WarningIcon fontSize="small" />
                Limited seats available
              </Box> */}
              </Box>

              <Typography variant="body2" className={classes.formFieldLabel}>
                Name*
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                required
                placeholder="Enter your full name"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                className={classes.formField}
                error={!!validationErrors.fullName}
                helperText={validationErrors.fullName}
                InputLabelProps={{ shrink: false }}
              />

              <Typography variant="body2" className={classes.formFieldLabel}>
                Email Address*
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                required
                type="email"
                placeholder="Enter your email address"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={classes.formField}
                error={!!validationErrors.email}
                helperText={validationErrors.email}
                InputLabelProps={{ shrink: false }}
              />

              <Box position="relative">
                <Typography variant="body2" className={classes.formFieldLabel}>
                  Phone Number*
                </Typography>
                <Box className={classes.phoneInputContainer}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    required
                    placeholder="Enter your phone number"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => {
                      let numericValue = e.target.value.replace(/[^0-9]/g, "");
                      if (selectedCountry.code === "IN") {
                        numericValue = numericValue.replace(/^0+/, "");

                        if (
                          numericValue.startsWith("91") &&
                          numericValue.length > 10
                        ) {
                          numericValue = numericValue.replace(/^91/, "");
                        }

                        if (numericValue.length > 10) {
                          return;
                        }
                      }
                      handleChange({
                        target: {
                          name: "phone",
                          value: numericValue,
                        },
                      });
                    }}
                    className={classes.formField}
                    error={!!validationErrors.phone}
                    helperText={validationErrors.phone}
                    InputLabelProps={{ shrink: false }}
                    type="tel"
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      maxLength: selectedCountry.code === "IN" ? 10 : undefined,
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment
                          position="start"
                          style={{ marginRight: 0 }}
                        >
                          <Box
                            className={classes.countryCodeInput}
                            style={{ cursor: "pointer" }}
                          >
                            <Select
                              id="countrySelectMobile"
                              value={selectedCountry.code}
                              className={classes.countrySelect}
                              onChange={(e) => {
                                const country = countries.find(
                                  (c) => c.code === e.target.value
                                );
                                if (country) {
                                  setSelectedCountry(country);
                                  let phoneWithoutCode = formData.phone
                                    .toString()
                                    .replace(/[^0-9]/g, "");
                                  // When switching to India, strip leading zeros and drop leading 91 only, do not slice
                                  if (country.code === "IN") {
                                    phoneWithoutCode = phoneWithoutCode.replace(
                                      /^0+/,
                                      ""
                                    );
                                    if (
                                      phoneWithoutCode.startsWith("91") &&
                                      phoneWithoutCode.length > 10
                                    ) {
                                      phoneWithoutCode =
                                        phoneWithoutCode.replace(/^91/, "");
                                    }
                                    if (phoneWithoutCode.length > 10) {
                                      // Do not allow exceeding 10 when switching to IN
                                      return;
                                    }
                                  }
                                  handleChange({
                                    target: {
                                      name: "phone",
                                      value: phoneWithoutCode,
                                    },
                                  });
                                }
                              }}
                              displayEmpty
                              renderValue={() => (
                                <Box display="flex" alignItems="center">
                                  <span
                                    style={{ marginRight: 4, fontSize: 16 }}
                                  >
                                    {selectedCountry.flag}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 14,
                                      fontWeight: 500,
                                      color: isDarkMode
                                        ? "#ffffffb5"
                                        : "#4a4a4a",
                                    }}
                                  >
                                    {selectedCountry.dial_code}
                                  </span>
                                </Box>
                              )}
                              MenuProps={{
                                anchorOrigin: {
                                  vertical: "bottom",
                                  horizontal: "left",
                                },
                                transformOrigin: {
                                  vertical: "top",
                                  horizontal: "left",
                                },
                                getContentAnchorEl: null,
                                PaperProps: {
                                  className: classes.mobileMenuPaper,
                                },
                                disableRestoreFocus: true,
                                autoFocus: false,
                              }}
                              style={{
                                width: "auto",
                                height: "100%",
                                padding: "0",
                                border: "none",
                                boxShadow: "none",
                              }}
                              disableUnderline={true}
                            >
                              <MenuItem
                                disableRipple
                                disableTouchRipple
                                style={{
                                  position: "sticky",
                                  top: 0,
                                  zIndex: 10,
                                  backgroundColor: isDarkMode
                                    ? "#000"
                                    : "#FFFFFF",
                                  opacity: 1,
                                }}
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                                onKeyDown={(e) =>
                                  e.nativeEvent.stopImmediatePropagation()
                                }
                              >
                                <TextField
                                  fullWidth
                                  placeholder="Search country..."
                                  value={countrySearch}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    setCountrySearch(e.target.value);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  onKeyDown={(e) =>
                                    e.nativeEvent.stopImmediatePropagation()
                                  }
                                  variant="standard"
                                  size="small"
                                  className={classes.countrySearchInput}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                      </InputAdornment>
                                    ),
                                  }}
                                  style={{
                                    borderRadius: "6px",
                                    padding: "4px 12px",
                                    border: `0.5px solid ${primaryColor}`,
                                    backgroundColor: isDarkMode
                                      ? "#000"
                                      : "#FFFFFF",
                                    color: isDarkMode ? "#FFFFFF" : "#000000",
                                  }}
                                />
                              </MenuItem>
                              {/* Use filtered countries for search functionality */}
                              {filteredCountries.length > 0 ? (
                                filteredCountries.map((country) => (
                                  <MenuItem
                                    key={country.code}
                                    value={country.code}
                                    style={{ minHeight: "40px" }}
                                  >
                                    <Box display="flex" alignItems="center">
                                      <span
                                        style={{ marginRight: 8, fontSize: 16 }}
                                      >
                                        {country.flag}
                                      </span>
                                      <span style={{ fontSize: 14 }}>
                                        {country.name}
                                      </span>
                                      <span
                                        style={{
                                          marginLeft: 8,
                                          color: "#666",
                                          fontSize: 14,
                                        }}
                                      >
                                        {country.dial_code}
                                      </span>
                                    </Box>
                                  </MenuItem>
                                ))
                              ) : (
                                <MenuItem disabled>No countries found</MenuItem>
                              )}
                            </Select>
                          </Box>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Box>

              <Typography variant="body2" className={classes.formFieldLabel}>
                Organisation Name*
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                required
                placeholder="Enter your organization name"
                name="organizationName"
                value={formData.organizationName}
                onChange={handleChange}
                className={classes.formField}
                error={!!validationErrors.organizationName}
                helperText={validationErrors.organizationName}
                InputLabelProps={{ shrink: false }}
              />

              <Typography variant="body2" className={classes.formFieldLabel}>
                Your Organisation Size*
              </Typography>
              <Box className={classes.radioGroup}>
                {organizationSizes.map((size) => (
                  <Box key={size} className={classes.radioButton}>
                    <label
                      className={`${classes.radioLabel} ${
                        formData.organizationSize === size
                          ? classes.radioLabelSelected
                          : ""
                      }`}
                    >
                      <Radio
                        checked={formData.organizationSize === size}
                        onChange={() =>
                          handleChange({
                            target: {
                              name: "organizationSize",
                              value: size,
                            },
                          })
                        }
                        className={classes.radioRoot}
                        style={{ color: primaryColor }}
                        size="small"
                      />
                      <Typography variant="body2">{size}</Typography>
                    </label>
                  </Box>
                ))}
              </Box>
              {validationErrors.organizationSize && (
                <Typography className={classes.radioErrorText}>
                  {validationErrors.organizationSize}
                </Typography>
              )}

              {/* Add web view proceed button */}
              {!isMobile && (
                <Button
                  fullWidth
                  className={classes.proceedButton}
                  onClick={handleProceed}
                  disabled={submitting}
                  style={{ marginTop: "24px" }}
                >
                  {submitting ? (
                    <CircularProgress size={24} style={{ color: "#ffffff" }} />
                  ) : (
                    "Continue to Select Tickets"
                  )}
                </Button>
              )}
            </Grid>
          </Grid>

          {isMobile && (
            <Box className={classes.stickyBottomBarWithLine}>
              <Box>
                <Typography className={classes.mobileTotalAmount}>
                  {/* <AccessTimeIcon
                    fontSize="small"
                    style={{
                      fontSize: 22,
                      verticalAlign: "middle",
                      marginRight: 4,
                      color: primaryColor,
                      fontWeight: 600,
                    }}
                  /> */}
                  <span
                    style={{
                      color: primaryColor,
                      fontWeight: 600,
                      fontSize: 14,
                      marginLeft: "10px",
                    }}
                  >
                    {/* {!eventCountdown.hasStarted
                      ? `Event starts in: ${eventCountdown.days}d ${eventCountdown.hours}h ${eventCountdown.minutes}m ${eventCountdown.seconds}s`
                      : "Event has started!"} */}
                    Limited Seats Available!
                  </span>
                </Typography>
              </Box>
              <Button
                className={classes.mobileProceedButton}
                onClick={handleProceed}
                disabled={submitting}
              >
                {submitting ? (
                  <CircularProgress size={20} style={{ color: "#000" }} />
                ) : (
                  "Register Now"
                )}
              </Button>
            </Box>
          )}
        </Container>
      </>
    );
  };

  // Render the passes step (now the second step)
  const renderPassesStep = () => {
    // Only include passes with quantity > 0 for summary
    const selectedPasses = passes.filter(
      (pass) => getCurrentQuantity(pass.id) > 0
    );

    const getAvailabilityTag = (passTitle) => {
      const spotsLeft = availableSpots[passTitle] || 0;

      if (spotsLeft <= 3) {
        return (
          <Box
            component="span"
            className={`${classes.remainingSpots} ${classes.almostGone}`}
          >
            <WarningIcon /> Only {spotsLeft} left!
          </Box>
        );
      } else if (spotsLeft < 10) {
        return (
          <Box component="span" className={classes.remainingSpots}>
            <WarningIcon /> {spotsLeft} spots left
          </Box>
        );
      }
      return null;
    };

    return (
      <>
        {/* Sticky Countdown Banner at Top */}
        <Box className={classes.stickyCountdownBanner}>
          <Typography className={classes.countdownText}>
            <AccessTimeIcon fontSize="small" style={{ fontSize: 20 }} />
            {!eventCountdown.hasStarted
              ? `Event starts in: ${eventCountdown.days}d ${eventCountdown.hours}h ${eventCountdown.minutes}m ${eventCountdown.seconds}s`
              : "🎉 Event has started! Secure your spot now!"}
          </Typography>
        </Box>

        <Container className={classes.container} style={{ marginTop: "60px" }}>
          {/* Add floating element for mobile with real countdown */}
          {/* {isMobile && (
            <Box className={classes.floatingBadge}>
              {timeLeft.days > 0 ? (
                <Typography
                  variant="body2"
                  style={{ fontSize: 12, fontWeight: 600 }}
                >
                  Final Countdown: {timeLeft.days}d {timeLeft.hours}h{" "}
                  {timeLeft.minutes}m
                </Typography>
              ) : (
                <Typography
                  variant="body2"
                  style={{ fontSize: 12, fontWeight: 600 }}
                >
                  LAST DAY! {timeLeft.hours}h {timeLeft.minutes}m left
                </Typography>
              )}
            </Box>
          )} */}

          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              {renderHeader()}

              <Box mb={2} display="flex" alignItems="center"></Box>

              <Box mb={2} display="none" alignItems="center">
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBack}
                  style={{ textTransform: "none" }}
                >
                  Back to Details
                </Button>
              </Box>

              {passes.map((pass) => {
                const isEarlyBird = pass.earlyBirdPrice || false;
                const isPopular = pass.isPopular || false;
                const isLimited = pass.isLimited || false;

                return (
                  <Paper
                    key={`${pass.id}-${pass.title}`}
                    className={`${classes.passCard} ${
                      isPopular
                        ? "popular-ticket"
                        : isEarlyBird
                        ? "early-bird-ticket"
                        : ""
                    }`}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      // mb={1}
                    >
                      <Typography variant="h6" className={classes.passTitle}>
                        {pass.title}
                        {isPopular && (
                          <Box
                            className={`${classes.ticketBadge} ${classes.popularBadge}`}
                          >
                            ⭐ Most Popular
                          </Box>
                        )}
                        {/* {isEarlyBird && (
                        <Box
                          className={`${classes.ticketBadge} ${classes.earlyBirdBadge}`}
                        >
                          🚀 Early Bird
                    </Box>
                  )} */}
                        {/* {isEarlyBird && (
                        <Box
                          className={`${classes.ticketBadge} ${classes.limitedBadge}`}
                        >
                          78% Filled
                        </Box>
                      )} */}
                      </Typography>
                    </Box>

                    {/* Early Bird Timer */}
                    {/* {isEarlyBird && (
                    <Box className={classes.timerContainer}>
                      <Typography className={classes.timerText}>
                        🔥 Early Bird offer ends in:
                      </Typography>
                      <Typography className={classes.timerDigits}>
                        {earlyBirdTimeLeft.days}d {earlyBirdTimeLeft.hours}h{" "}
                        {earlyBirdTimeLeft.minutes}m {earlyBirdTimeLeft.seconds}
                        s
                      </Typography>
                    </Box>
                  )} */}

                    <Typography
                      variant="body2"
                      className={classes.passDescription}
                    >
                      {pass.description}
                    </Typography>
                    {pass.title.includes("Single") && (
                      <Typography
                        variant="body2"
                        className={classes.multiMemberNote}
                      >
                        (Please choose Multi-member pass for 2 or more members)
                      </Typography>
                    )}

                    <List className={classes.featureList}>
                      {pass.features.map((feature, index) => (
                        <ListItem
                          key={index}
                          className={classes.featureItem}
                          disableGutters
                        >
                          <ListItemIcon className={classes.featureIcon}>
                            <FiberManualRecordIcon
                              className={classes.featureBullet}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={feature}
                            primaryTypographyProps={{
                              className: classes.featureText,
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>

                    <Box className={classes.passActions}>
                      <Typography className={classes.passPrice}>
                        {currencySymbol}
                        {pass.price}
                        {pass.originalPrice &&
                          pass.originalPrice > pass.price && (
                            <Typography
                              component="span"
                              className={classes.originalPrice}
                              style={{
                                display: "inline-block",
                                marginLeft: "6px",
                              }}
                            >
                              {currencySymbol}
                              {pass.originalPrice}
                            </Typography>
                          )}

                        {pass.id === "team" && (
                          <Typography
                            component="span"
                            className={classes.originalPrice}
                            style={{
                              display: "inline-block",
                              marginLeft: "6px",
                            }}
                          >
                            {currencySymbol}
                            {pass.beforePrice}
                          </Typography>
                        )}
                      </Typography>

                      {/* Show Add to Cart button or quantity control based on whether item is in cart */}
                      {getCurrentQuantity(pass.id) === 0 ? (
                        <Button
                          className={classes.addToCartButton}
                          onClick={() => {
                            // Use pass config directly (from backend)
                            if (pass.groupTicket) {
                              // Group tickets start at minimum group size
                              handleQuantityChange(
                                pass.id,
                                pass.minGroupSize || 2,
                                pass.title
                              );
                            } else {
                              // Individual tickets start at 1
                              handleQuantityChange(pass.id, 1, pass.title);
                            }
                          }}
                        >
                          Add to Cart
                        </Button>
                      ) : (
                        <Box className={classes.quantityControl}>
                          <IconButton
                            className={classes.quantityButton}
                            onClick={() =>
                              handleQuantityChange(pass.id, -1, pass.title)
                            }
                            disabled={getCurrentQuantity(pass.id) === 0}
                            size="small"
                          >
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                          <Typography
                            variant="body1"
                            className={classes.quantityValue}
                          >
                            {getCurrentQuantity(pass.id)}
                          </Typography>
                          <IconButton
                            className={classes.quantityPlusButton}
                            onClick={() =>
                              handleQuantityChange(pass.id, 1, pass.title)
                            }
                            disabled={
                              getCurrentQuantity(pass.id) >=
                              (pass.maxQuantity || 999)
                            }
                            size="small"
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                );
              })}

              {/* Add proper spacing for smooth scrolling */}
              <Box mt={4} mb={8}>
                {/* This creates space for better user experience and scroll behavior */}
              </Box>

              {/* Mobile summary now handled directly in sticky bottom bar */}
            </Grid>

            {!isMobile && (
              <Grid item xs={12} md={5}>
                <Card className={classes.summaryCard}>
                  <CardContent>
                    <Typography variant="h6" className={classes.summaryTitle}>
                      Order Summary
                    </Typography>

                    {selectedPasses.length === 0 ? (
                      <Box className={classes.emptyCartContainer}>
                        <ShoppingCartIcon className={classes.emptyCartIcon} />
                        <Typography
                          variant="body1"
                          align="center"
                          className={classes.emptyCartText}
                        >
                          Your cart is{" "}
                          <span className={classes.emptyCartHighlight}>
                            empty
                          </span>
                          !
                        </Typography>
                        <Typography
                          variant="body2"
                          align="center"
                          className={classes.emptyCartSubtext}
                        >
                          Secure your spot at this exclusive event and unlock
                          valuable WhatsApp business and AI resources.
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        {selectedPasses.map((pass) => (
                          <Box
                            key={`summary-${pass.id}-${pass.title}`}
                            className={classes.summaryItem}
                          >
                            <Typography
                              variant="body1"
                              className={classes.summaryItemName}
                            >
                              {pass.title}
                            </Typography>
                            <Typography
                              variant="body2"
                              className={classes.summaryItemQty}
                            >
                              x {getCurrentQuantity(pass.id)}
                            </Typography>
                            <Typography
                              variant="body1"
                              className={classes.summaryItemPrice}
                            >
                              {pricingData?.tickets?.find(
                                (t) => t.id === pass.id
                              )?.totalPrice ||
                                currencySymbol +
                                  (
                                    pass.price * getCurrentQuantity(pass.id)
                                  ).toFixed(2)}
                            </Typography>
                          </Box>
                        ))}

                        <Divider className={classes.divider} />

                        <Box className={classes.subtotalRow}>
                          <Typography
                            variant="body2"
                            style={{
                              color: isDarkMode ? "#D1D5DB" : "#4B5563",
                              fontWeight: 500,
                            }}
                          >
                            Sub Total
                          </Typography>
                          <Typography
                            variant="body2"
                            style={{
                              color: isDarkMode ? "#D1D5DB" : "#4B5563",
                              fontWeight: 500,
                            }}
                          >
                            {!pricingLoaded ? (
                              <Box
                                className={`${classes.priceSkeleton} ${classes.priceSkeletonMedium}`}
                              />
                            ) : (
                              `${currencySymbol}${getSubtotal().toFixed(2)}`
                            )}
                          </Typography>
                        </Box>

                        {(getDiscountAmount() > 0 || couponApplied) && (
                          <Box className={classes.subtotalRow}>
                            <Typography
                              variant="body2"
                              style={{
                                color: primaryColor,
                                fontWeight: 500,
                              }}
                            >
                              Discount{getCouponDisplayText()}
                            </Typography>
                            <Typography
                              variant="body2"
                              style={{
                                color: primaryColor,
                                fontWeight: 600,
                              }}
                            >
                              {!pricingLoaded ? (
                                <Box
                                  className={`${classes.priceSkeleton} ${classes.priceSkeletonMedium}`}
                                />
                              ) : (
                                `-${currencySymbol}${getDiscountAmount().toFixed(
                                  2
                                )}`
                              )}
                            </Typography>
                          </Box>
                        )}

                        {hasGst() && !eventConfig?.priceIncludeGST && (
                          <Box className={classes.subtotalRow}>
                            <Typography
                              variant="body2"
                              style={{
                                color: isDarkMode ? "#D1D5DB" : "#4B5563",
                                fontWeight: 500,
                              }}
                            >
                              {eventConfig?.gstLabel || "GST"}
                            </Typography>
                            <Typography
                              variant="body2"
                              style={{
                                color: isDarkMode ? "#D1D5DB" : "#4B5563",
                                fontWeight: 500,
                              }}
                            >
                              {!pricingLoaded ? (
                                <Box
                                  className={`${classes.priceSkeleton} ${classes.priceSkeletonMedium}`}
                                />
                              ) : (
                                `+${currencySymbol}${getGstAmount().toFixed(2)}`
                              )}
                            </Typography>
                          </Box>
                        )}

                        <Box className={classes.totalRow}>
                          <Typography
                            variant="subtitle1"
                            className={classes.totalLabel}
                          >
                            Total
                            {eventConfig?.priceIncludeGST && hasGst() && (
                              <Typography
                                component="span"
                                style={{
                                  fontSize: "0.75rem",
                                  fontWeight: 400,
                                  color: "#6B7280",
                                  marginLeft: "4px",
                                }}
                              >
                                (incl GST)
                              </Typography>
                            )}
                          </Typography>
                          <Typography
                            variant="subtitle1"
                            className={classes.totalAmount}
                          >
                            {!pricingLoaded ? (
                              <Box
                                className={`${classes.priceSkeleton} ${classes.priceSkeletonMedium}`}
                              />
                            ) : (
                              `${currencySymbol}${getTotalAmount().toFixed(2)}`
                            )}
                          </Typography>
                        </Box>
                      </>
                    )}

                    <div className={classes.couponSection}>
                      <Typography
                        variant="subtitle2"
                        className={classes.couponLabel}
                      >
                        Got a promo code?
                      </Typography>
                      <TextField
                        className={classes.couponTextField}
                        variant="outlined"
                        placeholder="Enter Code"
                        value={couponCode}
                        onChange={handleCouponChange}
                        fullWidth
                        inputProps={{
                          style: { fontSize: "14px" },
                          autoComplete: "off",
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Button
                                className={
                                  couponApplied
                                    ? classes.couponButtonIntegratedApplied
                                    : classes.couponButtonIntegrated
                                }
                                onClick={handleApplyCoupon}
                                disabled={
                                  validatingCoupon ||
                                  !couponCode.trim() ||
                                  couponApplied
                                }
                              >
                                {validatingCoupon ? (
                                  <CircularProgress
                                    size={18}
                                    style={{ color: "#fff" }}
                                  />
                                ) : couponApplied ? (
                                  "APPLIED"
                                ) : (
                                  "APPLY"
                                )}
                              </Button>
                            </InputAdornment>
                          ),
                        }}
                      />
                      {couponMessage && (
                        <Typography
                          variant="body2"
                          className={
                            couponApplied
                              ? classes.successText
                              : classes.errorText
                          }
                          style={{ marginTop: "8px" }}
                        >
                          {couponApplied && (
                            <CheckCircleIcon className={classes.successIcon} />
                          )}
                          {couponMessage}
                        </Typography>
                      )}
                    </div>

                    {/* Enhanced GST Details Section - Only show for INR events */}
                    {eventConfig?.currency === "INR" &&
                      eventConfig?.gstApplicable && (
                        <div className={classes.gstInputSection}>
                          <div className={classes.gstCheckboxContainer}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={showGstDetails}
                                  onChange={handleGstDetailsToggle}
                                  name="gstDetailsCheckbox"
                                  size="medium"
                                />
                              }
                              label="Add GST details"
                            />
                          </div>

                          <div
                            className={`${classes.gstDetailsCollapsible} ${
                              showGstDetails ? "expanded" : "collapsed"
                            }`}
                          >
                            <div className={classes.gstFieldContainer}>
                              <Typography className={classes.gstInputLabel}>
                                GST Number
                              </Typography>
                              <TextField
                                className={classes.gstTextField}
                                variant="outlined"
                                placeholder="Enter GST number"
                                value={gstNumber}
                                onChange={handleGstNumberChange}
                                error={!!gstNumberError}
                                helperText={gstNumberError}
                                fullWidth
                                inputProps={{
                                  maxLength: 15,
                                  style: { textTransform: "uppercase" },
                                }}
                              />
                            </div>

                            <div className={classes.gstFieldContainer}>
                              <Typography className={classes.gstInputLabel}>
                                Company Name
                              </Typography>
                              <TextField
                                className={classes.gstTextField}
                                variant="outlined"
                                placeholder="Enter company name"
                                value={companyName}
                                onChange={handleCompanyNameChange}
                                error={!!companyNameError}
                                helperText={companyNameError}
                                fullWidth
                              />
                            </div>

                            <div className={classes.gstFieldContainer}>
                              <Typography className={classes.gstInputLabel}>
                                Company Address
                              </Typography>
                              <TextField
                                className={classes.gstTextField}
                                variant="outlined"
                                placeholder="Enter complete company address with pincode"
                                value={companyAddress}
                                onChange={handleCompanyAddressChange}
                                error={!!companyAddressError}
                                helperText={companyAddressError}
                                fullWidth
                                multiline
                                rows={2}
                              />
                            </div>
                          </div>
                        </div>
                      )}

                    <Button
                      fullWidth
                      className={classes.proceedButton}
                      onClick={handleSubmit}
                      disabled={
                        !hasItems() ||
                        submitting ||
                        (showGstDetails &&
                          ((gstNumber && gstNumberError) ||
                            (companyName && companyNameError) ||
                            (companyAddress && companyAddressError)))
                      }
                    >
                      {submitting ? (
                        <CircularProgress
                          size={24}
                          style={{ color: "#ffffff" }}
                        />
                      ) : (
                        `Pay ${currencySymbol}${getTotalAmount().toFixed(2)}`
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>

          {/* Mobile Sticky Coupon Section - Variant 1 */}
          {isMobile && hasItems() && (
            <Box className={classes.mobileCouponSection}>
              {/* <Typography
              variant="subtitle2"
              className={classes.couponLabel}
              style={{ marginBottom: "12px" }}
            >
              Got a promo code?
            </Typography> */}
              <TextField
                className={classes.couponTextField}
                variant="outlined"
                placeholder="Have a coupon code? Type here"
                value={couponCode}
                onChange={handleCouponChange}
                fullWidth
                inputProps={{
                  style: { fontSize: "14px" },
                  autoComplete: "off",
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        className={
                          couponApplied
                            ? classes.couponButtonIntegratedApplied
                            : classes.couponButtonIntegrated
                        }
                        onClick={handleApplyCoupon}
                        disabled={
                          validatingCoupon ||
                          !couponCode.trim() ||
                          couponApplied
                        }
                      >
                        {validatingCoupon ? (
                          <CircularProgress
                            size={18}
                            style={{ color: "#fff" }}
                          />
                        ) : couponApplied ? (
                          "APPLIED"
                        ) : (
                          "APPLY"
                        )}
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
              {couponMessage && (
                <Typography
                  variant="body2"
                  className={
                    couponApplied ? classes.successText : classes.errorText
                  }
                >
                  {couponApplied && (
                    <CheckCircleIcon className={classes.successIcon} />
                  )}
                  {couponMessage}
                </Typography>
              )}
            </Box>
          )}

          {isMobile && hasItems() && (
            <Box
              className={classes.stickyBottomBar}
              style={{
                height: summaryExpanded ? "auto" : undefined,
                flexDirection: summaryExpanded ? "column" : "row",
                transition: "all 0.3s ease",
                padding: summaryExpanded
                  ? `${theme.spacing(2)}px`
                  : `${theme.spacing(1)}px ${theme.spacing(2)}px`,
              }}
            >
              {summaryExpanded ? (
                <>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    width="100%"
                    mb={1}
                  >
                    <Typography
                      variant="subtitle1"
                      style={{
                        fontWeight: 600,
                        color: isDarkMode ? "#FFFFFF" : "#1a1a1a",
                      }}
                    >
                      Order Summary
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setDrawerWasExpanded(true); // Track manual close
                        // Apply closing animation to summary content
                        const summaryContent =
                          document.querySelector(".summary-content");
                        if (summaryContent) {
                          summaryContent.style.animation =
                            "collapseSummary 0.15s ease";
                          // Wait briefly for animation to play before closing
                          setTimeout(() => setSummaryExpanded(false), 140);
                        } else {
                          setSummaryExpanded(false);
                        }
                      }}
                      style={{
                        padding: 4,
                        color: isDarkMode ? "#FFFFFF" : "#1a1a1a",
                      }}
                    >
                      <ExpandMoreIcon />
                    </IconButton>
                  </Box>

                  <Divider
                    style={{
                      width: "100%",
                      margin: "0 0 10px 0",
                      backgroundColor: isDarkMode
                        ? "rgba(255,255,255,0.1)"
                        : "#e0e0e0",
                    }}
                  />

                  <Box
                    className="summary-content"
                    style={{
                      maxHeight: "60vh",
                      overflowY: "auto",
                      width: "100%",
                      animation: "$expandSummary 0.1s ease-out", // Ultra-fast animation
                      padding: "0 8px",
                    }}
                  >
                    {selectedPasses.map((pass) => (
                      <Box
                        key={`mobile-summary-${pass.id}-${pass.title}`}
                        className={classes.summaryItem}
                        mb={1}
                      >
                        <Typography
                          variant="body1"
                          className={classes.summaryItemName}
                        >
                          {pass.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          className={classes.summaryItemQty}
                        >
                          x {getCurrentQuantity(pass.id)}
                        </Typography>
                        <Typography
                          variant="body1"
                          className={classes.summaryItemPrice}
                        >
                          {pricingData?.tickets?.find((t) => t.id === pass.id)
                            ?.totalPrice ||
                            currencySymbol +
                              (
                                pass.price * getCurrentQuantity(pass.id)
                              ).toFixed(2)}
                        </Typography>
                      </Box>
                    ))}

                    <Divider
                      style={{
                        margin: "10px 0",
                        backgroundColor: isDarkMode
                          ? "rgba(255,255,255,0.1)"
                          : "#e0e0e0",
                      }}
                    />

                    <Box className={classes.subtotalRow}>
                      <Typography
                        variant="body2"
                        style={{
                          color: isDarkMode ? "#D1D5DB" : "#4B5563",
                          fontWeight: 500,
                        }}
                      >
                        Sub Total
                      </Typography>
                      <Typography
                        variant="body2"
                        style={{
                          color: isDarkMode ? "#D1D5DB" : "#4B5563",
                          fontWeight: 500,
                        }}
                      >
                        {!pricingLoaded ? (
                          <Box
                            className={`${classes.priceSkeleton} ${classes.priceSkeletonMedium}`}
                          />
                        ) : (
                          `${currencySymbol}${getSubtotal().toFixed(2)}`
                        )}
                      </Typography>
                    </Box>

                    {(getDiscountAmount() > 0 || couponApplied) && (
                      <Box className={classes.subtotalRow}>
                        <Typography
                          variant="body2"
                          style={{
                            color: primaryColor,
                            fontWeight: 500,
                          }}
                        >
                          Discount{getCouponDisplayText()}
                        </Typography>
                        <Typography
                          variant="body2"
                          style={{
                            color: primaryColor,
                            fontWeight: 600,
                          }}
                        >
                          {!pricingLoaded ? (
                            <Box
                              className={`${classes.priceSkeleton} ${classes.priceSkeletonMedium}`}
                            />
                          ) : (
                            `-${currencySymbol}${getDiscountAmount().toFixed(
                              2
                            )}`
                          )}
                        </Typography>
                      </Box>
                    )}

                    {hasGst() && !eventConfig?.priceIncludeGST && (
                      <Box className={classes.subtotalRow}>
                        <Typography
                          variant="body2"
                          style={{
                            color: isDarkMode ? "#D1D5DB" : "#4B5563",
                            fontWeight: 500,
                          }}
                        >
                          {eventConfig?.gstLabel || "GST"}
                        </Typography>
                        <Typography
                          variant="body2"
                          style={{
                            color: isDarkMode ? "#D1D5DB" : "#4B5563",
                            fontWeight: 500,
                          }}
                        >
                          {!pricingLoaded ? (
                            <Box
                              className={`${classes.priceSkeleton} ${classes.priceSkeletonMedium}`}
                            />
                          ) : (
                            `+${currencySymbol}${getGstAmount().toFixed(2)}`
                          )}
                        </Typography>
                      </Box>
                    )}

                    <Box
                      className={classes.totalRow}
                      style={{ margin: `${theme.spacing(1)}px 0` }}
                    >
                      <Typography
                        variant="subtitle1"
                        className={classes.totalLabel}
                      >
                        Total
                        {eventConfig?.priceIncludeGST && hasGst() && (
                          <Typography
                            component="span"
                            style={{
                              fontSize: "0.75rem",
                              fontWeight: 400,
                              color: "#6B7280",
                              marginLeft: "4px",
                            }}
                          >
                            (incl GST)
                          </Typography>
                        )}
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        className={classes.totalAmount}
                      >
                        {currencySymbol}
                        {getTotalAmount().toFixed(2)}
                      </Typography>
                    </Box>

                    {/* Coupon Section in Mobile Summary */}
                    <div
                      className={classes.couponSection}
                      style={{ marginTop: "12px" }}
                    >
                      <Typography
                        variant="subtitle2"
                        className={classes.couponLabel}
                        style={{ marginBottom: "8px" }}
                      >
                        Got a promo code?
                      </Typography>
                      <TextField
                        className={classes.couponTextField}
                        variant="outlined"
                        placeholder="Enter Code"
                        value={couponCode}
                        onChange={handleCouponChange}
                        fullWidth
                        inputProps={{
                          style: { fontSize: "14px" },
                          autoComplete: "off",
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Button
                                className={
                                  couponApplied
                                    ? classes.couponButtonIntegratedApplied
                                    : classes.couponButtonIntegrated
                                }
                                onClick={handleApplyCoupon}
                                disabled={
                                  validatingCoupon ||
                                  !couponCode.trim() ||
                                  couponApplied
                                }
                              >
                                {validatingCoupon ? (
                                  <CircularProgress
                                    size={16}
                                    style={{ color: "#fff" }}
                                  />
                                ) : couponApplied ? (
                                  "APPLIED"
                                ) : (
                                  "APPLY"
                                )}
                              </Button>
                            </InputAdornment>
                          ),
                        }}
                      />
                      {couponMessage && (
                        <Typography
                          variant="body2"
                          className={
                            couponApplied
                              ? classes.successText
                              : classes.errorText
                          }
                          style={{ marginTop: "8px", fontSize: "12px" }}
                        >
                          {couponApplied && (
                            <CheckCircleIcon
                              className={classes.successIcon}
                              style={{ fontSize: "14px" }}
                            />
                          )}
                          {couponMessage}
                        </Typography>
                      )}
                    </div>

                    {/* Enhanced GST Details Section for Mobile - Only show for INR events */}
                    {eventConfig?.currency === "INR" &&
                      eventConfig?.gstApplicable && (
                        <div
                          className={classes.gstInputSection}
                          style={{ marginTop: "8px" }}
                        >
                          <div className={classes.gstCheckboxContainer}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={showGstDetails}
                                  onChange={handleGstDetailsToggle}
                                  name="gstDetailsCheckboxMobile"
                                  size="small"
                                />
                              }
                              label="Add GST details"
                            />
                          </div>

                          <div
                            className={`${classes.gstDetailsCollapsible} ${
                              showGstDetails ? "expanded" : "collapsed"
                            }`}
                          >
                            <div className={classes.gstFieldContainer}>
                              <Typography className={classes.gstInputLabel}>
                                GST Number *
                              </Typography>
                              <TextField
                                className={classes.gstTextField}
                                variant="outlined"
                                placeholder="Enter GST number"
                                value={gstNumber}
                                onChange={handleGstNumberChange}
                                error={!!gstNumberError}
                                helperText={gstNumberError}
                                fullWidth
                                inputProps={{
                                  maxLength: 15,
                                  style: { textTransform: "uppercase" },
                                }}
                              />
                            </div>

                            <div className={classes.gstFieldContainer}>
                              <Typography className={classes.gstInputLabel}>
                                Company Name
                              </Typography>
                              <TextField
                                className={classes.gstTextField}
                                variant="outlined"
                                placeholder="Enter company name"
                                value={companyName}
                                onChange={handleCompanyNameChange}
                                error={!!companyNameError}
                                helperText={companyNameError}
                                fullWidth
                              />
                            </div>

                            <div className={classes.gstFieldContainer}>
                              <Typography className={classes.gstInputLabel}>
                                Company Address
                              </Typography>
                              <TextField
                                className={classes.gstTextField}
                                variant="outlined"
                                placeholder="Enter complete company address"
                                value={companyAddress}
                                onChange={handleCompanyAddressChange}
                                error={!!companyAddressError}
                                helperText={companyAddressError}
                                fullWidth
                                multiline
                                rows={2}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                  </Box>

                  <Button
                    fullWidth
                    className={classes.proceedButton}
                    onClick={handleSubmit}
                    disabled={
                      submitting ||
                      !pricingLoaded ||
                      (showGstDetails &&
                        ((gstNumber && gstNumberError) ||
                          (companyName && companyNameError) ||
                          (companyAddress && companyAddressError)))
                    }
                    style={{ marginTop: "4px" }}
                  >
                    {submitting ? (
                      <CircularProgress
                        size={24}
                        style={{ color: "#ffffff" }}
                      />
                    ) : !pricingLoaded ? (
                      "Calculating..."
                    ) : (
                      `Pay ${currencySymbol}${getTotalAmount().toFixed(2)}`
                    )}
                  </Button>
                </>
              ) : (
                // Collapsed view - basic info
                <>
                  <Box>
                    <Typography className={classes.mobileTotal}>
                      Total
                      {eventConfig?.priceIncludeGST && hasGst() && (
                        <Typography
                          component="span"
                          style={{
                            fontSize: "0.75rem",
                            fontWeight: 400,
                            color: "#6B7280",
                            marginLeft: "4px",
                          }}
                        >
                          (incl GST){" "}
                        </Typography>
                      )}
                      :{" "}
                      {!pricingLoaded ? (
                        <Box
                          className={`${classes.priceSkeleton} ${classes.priceSkeletonMedium}`}
                          display="inline-block"
                          ml={1}
                          style={{ verticalAlign: "middle" }}
                        />
                      ) : (
                        <>
                          {getDiscountAmount() > 0 && (
                            <span
                              style={{
                                textDecoration: "line-through",
                                color: "#9e9e9e",
                                fontSize: "13px",
                                marginRight: "8px",
                              }}
                            >
                              {currencySymbol}
                              {(getTotalAmount() + getDiscountAmount()).toFixed(
                                2
                              )}
                            </span>
                          )}
                          <span
                            style={{
                              color:
                                getDiscountAmount() > 0
                                  ? primaryColor
                                  : "inherit",
                              fontWeight:
                                getDiscountAmount() > 0 ? 600 : "inherit",
                            }}
                          >
                            {currencySymbol} {getTotalAmount().toFixed(2)}
                          </span>
                        </>
                      )}
                    </Typography>
                    <Typography
                      className={classes.mobileTotalAmount}
                      onClick={() => {
                        setSummaryExpanded(true);
                        setDrawerWasExpanded(false); // Reset manual close tracking
                      }}
                      style={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        color: primaryColor,
                        fontWeight: 500,
                      }}
                    >
                      View Ticket Summary
                      <ExpandLessIcon
                        style={{
                          fontSize: 16,
                          marginLeft: 4,
                          transition: "transform 0.3s ease",
                        }}
                      />
                    </Typography>
                  </Box>
                  <Button
                    className={classes.mobileProceedButton}
                    onClick={handleSubmit}
                    disabled={submitting || !pricingLoaded}
                  >
                    {submitting ? (
                      <CircularProgress
                        size={20}
                        style={{ color: "#ffffff" }}
                      />
                    ) : !pricingLoaded ? (
                      "Loading..."
                    ) : (
                      `Buy Now`
                    )}
                  </Button>
                </>
              )}
            </Box>
          )}
        </Container>
      </>
    );
  };

  // Handle summary accordion expansion
  const handleSummaryToggle = () => {
    setSummaryExpanded(!summaryExpanded);
  };

  // Render the summary (right column)
  const renderSummary = () => {
    // Only include passes with quantity > 0
    const selectedPasses = passes.filter(
      (pass) => getCurrentQuantity(pass.id) > 0
    );
    const isEmpty = selectedPasses.length === 0;

    return (
      <Card className={classes.summaryCard}>
        <CardContent style={{ padding: "16px" }}>
          <Typography variant="h6" className={classes.summaryTitle}>
            Ticket Summary
          </Typography>

          {isEmpty ? (
            <Box className={classes.emptyCartContainer}>
              <ShoppingCartIcon className={classes.emptyCartIcon} />
              <Typography
                variant="body1"
                align="center"
                className={classes.emptyCartText}
              >
                Your cart is{" "}
                <span className={classes.emptyCartHighlight}>empty</span>!
              </Typography>
              <Typography
                variant="body2"
                align="center"
                className={classes.emptyCartSubtext}
              >
                Secure your spot at this exclusive event and unlock valuable
                WhatsApp business and AI resources.
              </Typography>
            </Box>
          ) : (
            <>
              {selectedPasses.map((pass) => (
                <Box
                  key={`summary-${pass.id}-${pass.title}`}
                  className={classes.summaryItem}
                >
                  <Typography
                    variant="body1"
                    className={classes.summaryItemName}
                  >
                    {pass.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    className={classes.summaryItemQty}
                  >
                    x {getCurrentQuantity(pass.id)}
                  </Typography>
                  <Typography
                    variant="body1"
                    className={classes.summaryItemPrice}
                  >
                    {pricingData?.tickets?.find((t) => t.id === pass.id)
                      ?.totalPrice ||
                      currencySymbol +
                        (pass.price * getCurrentQuantity(pass.id)).toFixed(2)}
                  </Typography>
                </Box>
              ))}

              <Divider className={classes.divider} />

              <Box className={classes.subtotalRow}>
                <Typography variant="body2">Sub Total</Typography>
                <Typography variant="body2">
                  {calculatingPricing ? (
                    <Box
                      className={`${classes.priceSkeleton} ${classes.priceSkeletonMedium}`}
                    />
                  ) : (
                    `${currencySymbol}${getSubtotal()}`
                  )}
                </Typography>
              </Box>

              {couponApplied && (
                <Box className={classes.subtotalRow}>
                  <Typography variant="body2" color="primary">
                    Discount
                  </Typography>
                  <Typography variant="body2" color="primary">
                    -{currencySymbol}
                    {discount}
                  </Typography>
                </Box>
              )}

              <Box className={classes.totalRow}>
                <Typography variant="subtitle1" className={classes.totalLabel}>
                  Total
                  {eventConfig?.priceIncludeGST && hasGst() && (
                    <Typography
                      component="span"
                      style={{
                        fontSize: "0.75rem",
                        fontWeight: 400,
                        color: "#6B7280",
                        marginLeft: "4px",
                      }}
                    >
                      (incl GST)
                    </Typography>
                  )}
                </Typography>
                <Typography variant="subtitle1" className={classes.totalAmount}>
                  {currencySymbol}
                  {getTotalAmount().toFixed(2)}
                </Typography>
              </Box>
            </>
          )}

          <div className={classes.couponSection}>
            <Typography variant="subtitle2" className={classes.couponLabel}>
              Got a promo code?
            </Typography>
            <div className={classes.couponInput}>
              <TextField
                className={classes.couponTextField}
                variant="outlined"
                placeholder="Enter Code"
                value={couponCode}
                onChange={handleCouponChange}
                fullWidth
              />
              <Button
                className={classes.couponButton}
                onClick={handleApplyCoupon}
                disabled={
                  validatingCoupon || !couponCode.trim() || couponApplied
                }
              >
                {validatingCoupon ? (
                  <CircularProgress size={20} style={{ color: primaryColor }} />
                ) : couponApplied ? (
                  "Applied"
                ) : (
                  "Apply"
                )}
              </Button>
            </div>
            {couponMessage && (
              <Typography
                variant="body2"
                className={
                  couponApplied ? classes.successText : classes.errorText
                }
              >
                {couponApplied && (
                  <CheckCircleIcon className={classes.successIcon} />
                )}
                {couponMessage}
              </Typography>
            )}
          </div>

          <Button
            fullWidth
            className={classes.proceedButton}
            onClick={handleProceed}
            disabled={!hasItems()}
          >
            Proceed
          </Button>
        </CardContent>
      </Card>
    );
  };

  // Render bottom sticky bar for mobile view
  const renderMobileFooter = () => {
    const hasSelectedItems = hasItems();

    if (!hasSelectedItems) {
      return null; // Don't show footer if no items selected
    }

    return (
      <Box className={classes.stickyBottomBar}>
        <Box>
          <Typography className={classes.mobileTotal}>
            Total
            {eventConfig?.priceIncludeGST && hasGst() && (
              <Typography
                component="span"
                style={{
                  fontSize: "0.75rem",
                  fontWeight: 400,
                  color: "#6B7280",
                  marginLeft: "4px",
                }}
              >
                (incl GST)
              </Typography>
            )}
            :{" "}
            {!pricingLoaded ? (
              <Box
                className={`${classes.priceSkeleton} ${classes.priceSkeletonMedium}`}
                display="inline-block"
                ml={1}
                style={{ verticalAlign: "middle" }}
              />
            ) : (
              `${currencySymbol} ${getTotalAmount().toFixed(2)}`
            )}
          </Typography>
        </Box>
        <Button
          className={classes.mobileProceedButton}
          onClick={handleProceed}
          disabled={!hasSelectedItems || !pricingLoaded}
        >
          {!pricingLoaded && hasSelectedItems ? "Calculating..." : "Proceed"}
        </Button>
      </Box>
    );
  };

  // Original countdown timer replaced with real-time countdown below

  // Add countdown timer component
  const CountdownTimer = () => {
    return (
      <Box className={classes.countdownTimer}>
        <Box className={classes.countdownUnit}>
          <Typography className={classes.countdownNumber}>
            {timeLeft.days}
          </Typography>
          <Typography className={classes.countdownLabel}>days</Typography>
        </Box>
        <Typography>:</Typography>
        <Box className={classes.countdownUnit}>
          <Typography className={classes.countdownNumber}>
            {timeLeft.hours}
          </Typography>
          <Typography className={classes.countdownLabel}>hrs</Typography>
        </Box>
        <Typography>:</Typography>
        <Box className={classes.countdownUnit}>
          <Typography className={classes.countdownNumber}>
            {timeLeft.minutes}
          </Typography>
          <Typography className={classes.countdownLabel}>mins</Typography>
        </Box>
      </Box>
    );
  };

  // Add useEffect for countdown timer that uses the actual end date
  useEffect(() => {
    // Set the date we're counting down to
    const countDownDate = new Date("April 25, 2025 10:59:59").getTime();

    // Define interval variable outside the updateCountdown function
    let interval;

    // Update the countdown every minute
    const updateCountdown = () => {
      // Get current date and time
      const now = new Date().getTime();

      // Find the distance between now and the countdown date
      const distance = countDownDate - now;

      // Calculate days, hours, minutes
      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));

      // If the countdown is over, stop the interval
      if (distance < 0) {
        clearInterval(interval);
        setTimeLeft({ days: 0, hours: 0, minutes: 0 });
      } else {
        setTimeLeft({ days, hours, minutes });
      }
    };

    // Run immediately once
    updateCountdown();

    // Then update every minute
    interval = setInterval(updateCountdown, 60000);

    // Clear interval on component unmount
    return () => clearInterval(interval);
  }, []);

  // Add a useEffect to inject meta tag to prevent auto-zoom on iOS


  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!isVerifyingPayment) {
        const message =
          "Are you sure you want to close this page? Your registration progress may be lost.";
        event.preventDefault();
        event.returnValue = message;
        return message;
      }
    };

    const handlePopState = (event) => {
      if (!isVerifyingPayment) {
        const shouldLeave = window.confirm(
          "Are you sure you want to leave this page? Your registration progress may be lost."
        );
        if (!shouldLeave) {
          window.history.pushState(null, null, window.location.pathname);
        }
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [isVerifyingPayment]);

  if (eventLoading || loading) {
    return (
      <div className={classes.loadingContainer}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          {/* Logo */}
          <Box mb={4} style={{ animation: "pulse 2s ease-in-out infinite" }}>
            <img
              src={AiSensyLogoLight}
              alt="AiSensy Logo"
              style={{ height: "40px", width: "auto" }}
            />
          </Box>

          {/* Loading Spinner */}
          <CircularProgress
            size={50}
            thickness={4}
            style={{ color: primaryColor }}
          />

          {/* Loading Text */}
          <Typography
            variant="h6"
            style={{
              marginTop: 24,
              color: isDarkMode ? "#FFFFFF" : "#1F2937",
              fontWeight: 600,
              fontSize: "18px",
            }}
          >
            {eventLoading ? "Loading event details..." : "Loading..."}
          </Typography>

          {/* Subtitle */}
          <Typography
            variant="body2"
            style={{
              marginTop: 8,
              color: isDarkMode ? "#9CA3AF" : "#6B7280",
              fontSize: "14px",
            }}
          >
            Please wait while we prepare everything for you
          </Typography>
        </Box>
      </div>
    );
  }

  // Show error if event not found
  if (!eventConfig && !eventLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100vh"
        textAlign="center"
        px={2}
      >
        {/* <ErrorOutlineIcon color="error" sx={{ fontSize: 60, mb: 2 }} /> */}
        <Typography variant="h4" color="error" fontWeight="bold">
          Event Not Found
        </Typography>
        <Typography variant="body1" sx={{ mt: 1, color: "text.secondary" }}>
          {error ||
            "The event you're looking for doesn't exist or may have been removed."}
        </Typography>

        <Button
          variant="contained"
          color="primary"
          sx={{ mt: 3, borderRadius: 2, px: 3 }}
          onClick={() => (window.location.href = "/evolve-blr")}
        >
          Go Back Home
        </Button>
      </Box>
    );
  }

  return (
    <div className={classes.root}>
      {/* Modern Dark Mode Toggle */}
      <Box
        position="fixed"
        top={20}
        right={20}
        zIndex={1000}
        display="none"
        alignItems="center"
        gap={1.5}
        bgcolor={
          isDarkMode ? "rgba(28, 28, 30, 0.95)" : "rgba(255, 255, 255, 0.95)"
        }
        borderRadius={3}
        px={2.5}
        py={1.5}
        boxShadow={
          isDarkMode
            ? "0 4px 20px rgba(0,0,0,0.4)"
            : "0 4px 20px rgba(0,0,0,0.1)"
        }
        sx={{
          backdropFilter: "blur(10px)",
          border: isDarkMode
            ? `1px solid ${fade(primaryColor, 0.2)}`
            : "1px solid rgba(0,0,0,0.1)",
          transition: "all 0.3s ease",
          "&:hover": {
            transform: "translateY(-1px)",
            boxShadow: isDarkMode
              ? "0 6px 25px rgba(0,0,0,0.5)"
              : "0 6px 25px rgba(0,0,0,0.15)",
          },
        }}
      >
        <Typography
          variant="body2"
          color={isDarkMode ? "#FFFFFF" : "#1a1a1a"}
          sx={{
            fontSize: "13px",
            fontWeight: 600,
            letterSpacing: "0.5px",
            textTransform: "uppercase",
          }}
        >
          {isDarkMode ? "Dark" : "Light"}
        </Typography>
        <Box
          sx={{
            position: "relative",
            width: 50,
            height: 26,
            borderRadius: 13,
            backgroundColor: isDarkMode ? primaryColor : "#e0e0e0",
            cursor: "pointer",
            transition: "all 0.3s ease",
            "&:hover": {
              backgroundColor: isDarkMode
                ? darken(primaryColor, 0.1)
                : "#d0d0d0",
            },
          }}
          onClick={handleDarkModeToggle}
        >
          <Box
            sx={{
              position: "absolute",
              top: 2,
              left: isDarkMode ? 24 : 2,
              width: 22,
              height: 22,
              borderRadius: "50%",
              backgroundColor: "#FFFFFF",
              transition: "all 0.3s ease",
              boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isDarkMode ? (
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: "#1C1C1E",
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: "#FFD700",
                }}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* Render appropriate step based on activeStep */}
      {activeStep === 0 ? renderUserDetailsStep() : renderPassesStep()}

      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        style={{ zIndex: 10001, marginTop: "60px" }}
      >
        <Alert
          onClose={handleCloseError}
          severity="error"
          variant="filled"
          style={{ minWidth: "300px" }}
        >
          {String(error || "An error occurred")}
        </Alert>
      </Snackbar>

      <Snackbar
        open={showQuantityMsg}
        autoHideDuration={4000}
        onClose={handleCloseQuantityMsg}
        message={quantityMsg}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        ContentProps={{
          style: {
            backgroundColor: "#424242",
            fontWeight: 500,
          },
        }}
      />
    </div>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(8),
    background: (props) =>
      props.isDarkMode
        ? `rgb(10, 10, 10, 1)`
        : `linear-gradient(135deg, #f9fcfa 0%, #f5f5f5 100%)`,
    backgroundImage: (props) =>
      props.isDarkMode
        ? `none`
        : `radial-gradient(circle at 25% 10%, ${fade(
            props.primaryColor,
            0.05
          )} 0%, ${fade(props.primaryColor, 0)} 20%),
         radial-gradient(circle at 75% 90%, ${fade(
           props.primaryColor,
           0.05
         )} 0%, ${fade(props.primaryColor, 0)} 20%),
         linear-gradient(135deg, #f9fcfa 0%, #f5f5f5 100%)`,
    backgroundAttachment: "fixed",
    position: "relative",
    minHeight: "100vh",
    overscrollBehavior: "none",
    WebkitOverflowScrolling: "touch",
    "&:before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "180px",
      background: (props) =>
        props.isDarkMode
          ? `none`
          : `linear-gradient(180deg, ${fade(
              props.primaryColor,
              0.08
            )} 0%, ${fade(props.primaryColor, 0)} 100%)`,
      zIndex: 0,
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(3, 0.3),
      paddingTop: theme.spacing(0.5),
      paddingBottom: theme.spacing(6),
    },
  },

  // Header and branding
  headerLogo: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      justifyContent: "center",
    },
  },
  logo: {
    height: 80,
  },
  eventBadges: {
    display: "none",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      justifyContent: "center",
      marginBottom: theme.spacing(1),
    },
  },
  eventBadge: {
    backgroundColor: (props) => fade(props.primaryColor, 0.08),
    color: (props) => props.primaryColor,
    fontWeight: 500,
    border: (props) => `1px solid ${fade(props.primaryColor, 0.15)}`,
    "& .MuiChip-icon": {
      color: (props) => props.primaryColor,
    },
  },
  businessChip: {
    backgroundColor: "#FFF8E1",
    color: "#FF9800",
    fontWeight: 500,
    border: "1px solid #FFE0B2",
    "& .MuiChip-icon": {
      color: "#FF9800",
    },
  },
  dubaiChip: {
    backgroundColor: "#FFF8E1",
    color: "#FF9800",
    fontWeight: 500,
    border: "1px solid #FFE0B2",
    "& .MuiChip-icon": {
      color: "#FF9800",
    },
  },
  whatsappChip: {
    backgroundColor: (props) => fade(props.primaryColor, 0.1),
    color: "#25D366",
    fontWeight: 500,
    border: "1px solid #C8E6C9",
    "& .MuiChip-icon": {
      color: "#25D366",
    },
  },
  aiChip: {
    backgroundColor: "#E3F2FD",
    color: "#2196F3",
    fontWeight: 500,
    border: "1px solid #BBDEFB",
    "& .MuiChip-icon": {
      color: "#2196F3",
    },
  },

  container: {
    position: "relative",
    zIndex: 1,
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(0, 1),
      paddingBottom: theme.spacing(22), // Add padding for sticky coupon section + sticky bar
    },
  },
  eventTitle: {
    fontSize: "28px",
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    fontFamily: "'Inter', 'Helvetica', 'Arial', sans-serif",
    letterSpacing: "0.2px",
    color: (props) => (props.isDarkMode ? "#FFFFFF" : "#1a1a1a"),
    [theme.breakpoints.down("sm")]: {
      textAlign: "center",
      fontSize: "24px",
      marginBottom: theme.spacing(2),
    },
  },
  eventDate: {
    display: "flex",
    alignItems: "center",
    // marginBottom: theme.spacing(3),
    color: (props) => (props.isDarkMode ? "#ffffffb5" : "#666"),
    // backgroundColor: (props) => fade(props.primaryColor, 0.08),
    padding: theme.spacing(1, 0),
    borderRadius: "20px",
    display: "inline-flex",
    fontSize: "14px",
    fontWeight: 600,
    [theme.breakpoints.down("sm")]: {
      justifyContent: "center",
      // marginBottom: theme.spacing(4),
      width: "100%",
    },
  },
  eventDateText: {
    fontWeight: 600,
    letterSpacing: "0.3px",
  },
  lucideIcon: {
    marginRight: theme.spacing(1),
    verticalAlign: "middle",
    color: "inherit",
  },
  // Left Column Styles
  passCard: {
    padding: theme.spacing(2),
    borderRadius: "12px",
    boxShadow: (props) =>
      props.isDarkMode
        ? "0 2px 12px rgba(0,0,0,0.3)"
        : "0 2px 12px rgba(0,0,0,0.04)",
    marginBottom: theme.spacing(2),
    backgroundColor: (props) => (props.isDarkMode ? "#000" : "#ffffff"),
    border: (props) => `1px solid ${fade(props.primaryColor, 0.2)}`,
    transition: "transform 0.2s, box-shadow 0.2s",
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "5px",
      height: "100%",
      background: (props) =>
        `linear-gradient(to bottom, ${props.primaryColor}, ${props.primaryColor}aa)`,
      borderTopLeftRadius: "12px",
      borderBottomLeftRadius: "12px",
    },
    // "&:after": {
    //   content: '""',
    //   position: "absolute",
    //   bottom: 15,
    //   right: 15,
    //   width: "80px",
    //   height: "80px",
    //   backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='24' height='24'%3E%3Cpath fill='none' d='M0 0h24v24H0z'/%3E%3Cpath d='M7.253 18.494l.724.423A7.953 7.953 0 0 0 12 20a8 8 0 1 0-8-8c0 1.436.377 2.813 1.084 4.024l.422.724-.653 2.401 2.4-.655zM2.004 22l1.352-4.968A9.954 9.954 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10a9.954 9.954 0 0 1-5.03-1.355L2.004 22zM8.391 7.308c.134-.01.269-.01.403-.004.054.004.108.01.162.016.159.018.334.115.393.249.298.676.588 1.357.868 2.04.062.152.025.347-.093.537a4.38 4.38 0 0 1-.263.372c-.113.145-.356.411-.356.411s-.099.118-.061.265c.014.056.06.137.102.205l.059.095c.256.427.6.86 1.02 1.268.12.116.237.235.363.346.468.413.998.75 1.57 1l.005.002c.085.037.128.057.252.11.062.026.126.049.191.066a.35.35 0 0 0 .367-.13c.724-.877.79-.934.796-.934v.002a.482.482 0 0 1 .378-.127c.06.004.121.015.177.04.531.243 1.4.622 1.4.622l.582.261c.098.047.187.158.19.265.004.067.01.175-.013.373-.032.259-.11.565-.217.793-.124.27-.353.45-.646.478-.156.014-.289.017-.424.012-.142-.007-.326-.06-.453-.074-.08-.008-.158-.018-.232-.036-.458-.115-.911-.395-1.24-.644-.55-.41-.856-.685-1.29-1.137-.882-.93-1.49-1.802-1.884-2.779-.1-.248-.176-.497-.255-.747-.034-.107-.064-.214-.094-.32-.064-.227-.125-.455-.18-.682-.115-.466-.36-.958-.134-1.529.097-.231.263-.364.466-.412z' fill='rgba(37, 211, 102, 0.07)'/%3E%3C/svg%3E")`,
    //   backgroundRepeat: "no-repeat",
    //   backgroundSize: "contain",
    //   opacity: 0.3,
    //   pointerEvents: "none",
    // },
    [theme.breakpoints.down("sm")]: {
      marginBottom: theme.spacing(1.7),
    },
  },
  passTitle: {
    fontSize: "18px",
    fontWeight: 700,
    marginBottom: theme.spacing(1.5),
    color: (props) => (props.isDarkMode ? "#FFFFFF" : "#1a1a1a"),
    fontFamily: "'Inter', 'Helvetica', 'Arial', sans-serif",
    lineHeight: 1.3,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  passDescription: {
    color: (props) => (props.isDarkMode ? "#ffffffb5" : "#666"),
    marginBottom: theme.spacing(1),
    lineHeight: 1.5,
    fontSize: "14px",
  },
  // New styles for enhanced tickets
  ticketBadge: {
    padding: "4px 12px",
    borderRadius: "20px",
    fontSize: "11px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  popularBadge: {
    backgroundColor: (props) => props.primaryColor,
    color: "white",
  },
  earlyBirdBadge: {
    backgroundColor: "#ff9800ff",
    color: "white",
  },
  limitedBadge: {
    backgroundColor: (props) => props.primaryColor,
    color: "black",
  },
  timerContainer: {
    backgroundColor: "#fff3e0",
    borderRadius: "8px",
    padding: theme.spacing(1),
    marginTop: theme.spacing(1),
    border: "1px solid #ffcc02",
  },
  timerText: {
    color: "#e65100",
    fontWeight: 600,
    fontSize: "13px",
  },
  timerDigits: {
    color: "#bf360c",
    fontWeight: 700,
    fontSize: "14px",
    fontFamily: "'Inter', 'Helvetica', 'Arial', sans-serif",
  },
  // Skeleton and loading styles
  pricingSkeleton: {
    backgroundColor: "#f5f5f5",
    borderRadius: "8px",
    marginBottom: theme.spacing(1),
    animation: "$pulse 1.5s ease-in-out infinite",
  },
  skeletonLine: {
    height: "20px",
    borderRadius: "4px",
    marginBottom: theme.spacing(0.5),
  },
  skeletonLineSmall: {
    height: "16px",
    borderRadius: "4px",
    marginBottom: theme.spacing(0.5),
    width: "70%",
  },
  skeletonButton: {
    height: "48px",
    borderRadius: "8px",
    marginTop: theme.spacing(1),
  },
  // Price skeleton for order summary amounts only
  priceSkeleton: {
    backgroundColor: "#f5f5f5",
    borderRadius: "4px",
    animation: "$pulse 1.5s ease-in-out infinite",
    display: "inline-block",
  },
  priceSkeletonSmall: {
    width: "60px",
    height: "16px",
  },
  priceSkeletonMedium: {
    width: "80px",
    height: "20px",
  },
  priceSkeletonLarge: {
    width: "120px",
    height: "24px",
  },
  summaryItemSkeleton: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  summaryItemNameSkeleton: {
    width: "120px",
    height: "16px",
  },
  "@keyframes pulse": {
    "0%": {
      opacity: 1,
    },
    "50%": {
      opacity: 0.6,
    },
    "100%": {
      opacity: 1,
    },
  },
  featureList: {
    padding: 0,
    marginBottom: theme.spacing(1.5),
  },
  featureItem: {
    padding: theme.spacing(0, 0),
  },
  featureIcon: {
    minWidth: "20px",
    color: (props) => (props.isDarkMode ? "#FFFFFF" : "#000"),
  },
  featureBullet: {
    fontSize: "8px",
    color: (props) =>
      props.isDarkMode ? `${props.primaryColor}` : `${props.primaryColor}`,
  },
  featureText: {
    fontSize: "13px",
    lineHeight: 1.4,
    color: (props) => (props.isDarkMode ? "#FFFFFF" : "#444"),
    fontFamily: "'Inter', 'Helvetica', 'Arial', sans-serif",
  },
  passActions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: theme.spacing(1.5),
    paddingTop: theme.spacing(1),
    borderTop: (props) =>
      props.isDarkMode
        ? `1px solid ${fade(props.primaryColor, 0.2)}`
        : "1px solid rgba(0,0,0,0.05)",
  },
  passPrice: {
    fontSize: "22px",
    fontWeight: 700,
    color: (props) =>
      props.isDarkMode ? `${props.primaryColor}` : `${props.primaryColor}`,
    fontFamily: "'Inter', 'Helvetica', 'Arial', sans-serif",
  },
  addToCartButton: {
    backgroundColor: (props) =>
      props.isDarkMode ? `${props.primaryColor}` : `${props.primaryColor}`,
    color: (props) => (props.isDarkMode ? "#000" : "#fff"),
    padding: "10px 18px",
    borderRadius: "4px",
    fontWeight: 600,
    textTransform: "none",
    fontSize: "14px",
    fontFamily: "'Inter', 'Helvetica', 'Arial', sans-serif",
    "&:hover": {
      backgroundColor: (props) =>
        props.isDarkMode
          ? darken(props.primaryColor, 0.1)
          : `${darken(props.primaryColor, 0.1)}`,
    },
  },
  quantityControl: {
    display: "flex",
    alignItems: "center",
    border: (props) =>
      props.isDarkMode
        ? `1px solid ${fade(props.primaryColor, 0.3)}`
        : `1px solid #eaeaea`,
    borderRadius: "50px",
    padding: "4px",
    backgroundColor: (props) =>
      props.isDarkMode
        ? fade(props.primaryColor, 0.1)
        : fade(props.primaryColor, 0.08),
  },
  quantityButton: {
    padding: 0,
    minWidth: "35px",
    height: "35px",
    borderRadius: "50%",
    backgroundColor: (props) => (props.isDarkMode ? "#1C1C1E" : "#ffffff"),
    boxShadow: (props) =>
      props.isDarkMode
        ? "0 1px 3px rgba(0,0,0,0.3)"
        : "0 1px 3px rgba(0,0,0,0.1)",
    color: (props) =>
      props.isDarkMode ? `${props.primaryColor}` : `${props.primaryColor}`,
    border: (props) =>
      props.isDarkMode
        ? `1px solid ${fade(props.primaryColor, 0.3)}`
        : `1px solid ${fade(props.primaryColor, 0.15)}`,
    "&:hover": {
      backgroundColor: (props) =>
        props.isDarkMode
          ? fade(props.primaryColor, 0.1)
          : fade(props.primaryColor, 0.15),
    },
  },
  quantityPlusButton: {
    padding: 0,
    minWidth: "35px",
    height: "35px",
    borderRadius: "50%",
    backgroundColor: (props) => props.primaryColor,
    boxShadow: (props) => `0 1px 5px ${fade(props.primaryColor, 0.3)}`,
    color: "#ffffff",
    border: (props) => `1px solid ${props.primaryColor}`,
    "&:hover": {
      backgroundColor: (props) => darken(props.primaryColor, 0.1),
    },
  },
  quantityValue: {
    margin: "0 8px",
    minWidth: "12px",
    textAlign: "center",
    fontWeight: 600,
    fontSize: "14px",
    color: (props) => (props.isDarkMode ? "#FFFFFF" : "#1a1a1a"),
  },
  addToCartText: {
    fontSize: "13px",
    color: "#666",
    fontStyle: "italic",
    marginTop: theme.spacing(0.5),
  },
  multiMemberNote: {
    fontSize: "12px",
    color: "#666",
    fontStyle: "italic",
    marginTop: theme.spacing(0.5),
    fontWeight: 500,
  },

  // Right Column Styles
  summaryCard: {
    borderRadius: "12px",
    boxShadow: (props) =>
      props.isDarkMode
        ? "0 2px 12px rgba(0,0,0,0.3), 0 1px 3px rgba(0,0,0,0.2)"
        : "0 2px 12px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.08)",
    backgroundColor: (props) => (props.isDarkMode ? "#000" : "#ffffff"),
    position: "sticky",
    top: "20px",
    border: (props) =>
      props.isDarkMode
        ? `1px solid ${fade(props.primaryColor, 0.2)}`
        : `1px solid ${fade(props.primaryColor, 0.12)}`,
    transition: "all 0.3s ease",
    overflow: "hidden",
    backdropFilter: "blur(10px)",
    "&:hover": {
      boxShadow: (props) =>
        props.isDarkMode
          ? `0 4px 20px rgba(0,0,0,0.4), 0 2px 8px ${fade(
              props.primaryColor,
              0.1
            )}`
          : `0 4px 20px rgba(0,0,0,0.1), 0 2px 8px ${fade(
              props.primaryColor,
              0.1
            )}`,
      transform: "translateY(-1px)",
    },
    "&:after": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      width: "100%",
      height: "2px",
      background: (props) =>
        props.isDarkMode
          ? `linear-gradient(90deg, ${props.primaryColor} 0%, ${darken(
              props.primaryColor,
              0.1
            )} 100%)`
          : `linear-gradient(90deg, ${props.primaryColor} 0%, ${darken(
              props.primaryColor,
              0.15
            )} 100%)`,
      borderTopLeftRadius: "12px",
      borderTopRightRadius: "12px",
    },
  },
  summaryTitle: {
    fontSize: "20px",
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
    borderBottom: (props) =>
      props.isDarkMode
        ? `2px solid ${props.primaryColor}`
        : `2px solid ${props.primaryColor}`,
    paddingBottom: theme.spacing(0.5),
    display: "inline-block",
    fontFamily: "'Inter', 'Helvetica', 'Arial', sans-serif",
    letterSpacing: "0.2px",
    color: (props) => (props.isDarkMode ? "#FFFFFF" : "inherit"),
  },
  summaryItem: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(0.8),
    alignItems: "center",
    padding: theme.spacing(0.5, 0),
  },
  summaryItemName: {
    flexGrow: 0.8,
    fontSize: "15px",
    fontFamily: "'Inter', 'Helvetica', 'Arial', sans-serif",
    fontWeight: 500,
    color: (props) => (props.isDarkMode ? "#FFFFFF" : "#333"),
  },
  summaryItemQty: {
    marginRight: theme.spacing(1),
    color: (props) => (props.isDarkMode ? "#ffffffb5" : "#555"),
    width: "24px",
    textAlign: "center",
    fontSize: "14px",
    fontWeight: 500,
  },
  summaryItemPrice: {
    fontWeight: 600,
    // width: "60px",
    textAlign: "right",
    fontSize: "15px",
    color: (props) => (props.isDarkMode ? "#FFFFFF" : "#222"),
    fontFamily: "'Inter', 'Helvetica', 'Arial', sans-serif",
  },
  divider: {
    margin: theme.spacing(1, 0),
  },
  subtotalRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(0.5),
    fontSize: "14px",
    fontFamily: "'Inter', 'Helvetica', 'Arial', sans-serif",
    color: (props) => (props.isDarkMode ? "#ffffffb5" : "#666"),
  },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    backgroundColor: (props) => `${props.secondaryColor}`,
    padding: theme.spacing(1),
    borderRadius: "6px",
  },
  totalLabel: {
    fontSize: "15px",
    fontWeight: 600,
    color: (props) => (props.isDarkMode ? "#FFFFFF" : "#222"),
    fontFamily: "'Inter', 'Helvetica', 'Arial', sans-serif",
  },
  totalAmount: {
    fontSize: "17px",
    fontWeight: 700,
    color: (props) =>
      props.isDarkMode ? `${props.primaryColor}` : `${props.primaryColor}`,
    fontFamily: "'Inter', 'Helvetica', 'Arial', sans-serif",
  },
  couponSection: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  couponLabel: {
    marginBottom: theme.spacing(0.5),
    fontWeight: 600,
    fontSize: "14px",
    color: (props) => (props.isDarkMode ? "#FFFFFF" : "#222"),
    fontFamily: "'Inter', 'Helvetica', 'Arial', sans-serif",
  },
  couponInput: {
    display: "flex",
    alignItems: "center",
  },
  couponTextField: {
    flexGrow: 1,
    marginRight: theme.spacing(1),
    borderRadius: "6px",
    backgroundColor: (props) => (props.isDarkMode ? "#000" : "#fff"),
    border: (props) =>
      props.isDarkMode
        ? `0.5px solid ${props.primaryColor}`
        : `0.5px solid ${props.primaryColor}`,
    "& .MuiOutlinedInput-root": {
      borderRadius: "4px",
      paddingRight: "4px",
      "& fieldset": {
        borderColor: (props) => props.primaryColor,
      },
      "&:hover fieldset": {
        borderColor: (props) => props.primaryColor,
      },
      "&.Mui-focused fieldset": {
        borderColor: (props) =>
          props.isDarkMode ? `${props.primaryColor}` : `${props.primaryColor}`,
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "12px 14px",
      fontSize: "15px",
      color: (props) => (props.isDarkMode ? "#FFFFFF" : "inherit"),
    },
    "&.MuiTextField-root": {
      padding: "0px",
    },
    "& .MuiInputAdornment-root": {
      marginLeft: 0,
    },
  },
  couponButton: {
    height: "42px",
    padding: "0 12px",
    backgroundColor: (props) => (props.isDarkMode ? "#2a2a2a" : "#ffffff"),
    color: (props) => (props.isDarkMode ? `#FFFFFF` : `${props.primaryColor}`),
    border: (props) =>
      props.isDarkMode
        ? `1px solid ${props.primaryColor}`
        : `1px solid ${props.primaryColor}`,
    borderRadius: "6px",
    fontWeight: 600,
    textTransform: "none",
    "&:hover": {
      backgroundColor: (props) =>
        props.isDarkMode
          ? fade(props.primaryColor, 0.1)
          : fade(props.primaryColor, 0.08),
    },
    "&.Mui-disabled": {
      backgroundColor: (props) =>
        props.isDarkMode
          ? fade(props.primaryColor, 0.05)
          : fade(props.primaryColor, 0.08),
      color: (props) =>
        props.isDarkMode
          ? fade(props.primaryColor, 0.5)
          : fade(props.primaryColor, 0.5),
      border: (props) =>
        props.isDarkMode
          ? `1px solid ${fade(props.primaryColor, 0.2)}`
          : `1px solid ${fade(props.primaryColor, 0.25)}`,
      opacity: 0.8,
    },
  },
  successText: {
    color: (props) => props.primaryColor,
    display: "flex",
    alignItems: "center",
    marginTop: theme.spacing(0.5),
    fontSize: "12px",
  },
  successIcon: {
    marginRight: theme.spacing(0.5),
    fontSize: "12px",
    color: (props) => props.primaryColor,
  },
  errorText: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(0.5),
    fontSize: "12px",
  },
  proceedButton: {
    padding: theme.spacing(1.25, 0),
    backgroundColor: (props) =>
      props.isDarkMode ? `${props.primaryColor}` : `${props.primaryColor}`,
    color: (props) => (props.isDarkMode ? "#000" : "#ffffff"),
    "&:hover": {
      backgroundColor: (props) => darken(props.primaryColor, 0.1),
    },
    marginTop: theme.spacing(1.5),
    borderRadius: "4px",
    fontWeight: 600,
    fontSize: "15px",
    textTransform: "none",
    "&.Mui-disabled": {
      backgroundColor: "#888888 !important",
      color: "#ffffff !important",
      opacity: "1 !important",
    },
    // Ensure loading state is visible
    "&[disabled]": {
      backgroundColor: "#888888 !important",
      color: "#ffffff !important",
      opacity: "1 !important",
    },
  },

  // Empty cart styles
  emptyCartContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(3.3, 2),
    marginBottom: theme.spacing(2),
    backgroundColor: (props) =>
      props.isDarkMode
        ? `${fade(props.primaryColor, 0.05)}`
        : `${fade(props.primaryColor, 0.03)}`,
    borderRadius: "10px",
    border: (props) =>
      props.isDarkMode
        ? `1px dashed ${props.primaryColor}`
        : `1px dashed ${fade(props.primaryColor, 0.3)}`,
  },
  emptyCartIcon: {
    fontSize: "54px",
    color: (props) =>
      props.isDarkMode
        ? `${fade(props.primaryColor, 0.7)}`
        : `${fade(props.primaryColor, 0.7)}`,
    marginBottom: theme.spacing(2),
  },
  emptyCartText: {
    color: (props) => (props.isDarkMode ? "#ffffffb5" : "#444"),
    textAlign: "center",
    fontFamily: "'Inter', 'Helvetica', 'Arial', sans-serif",
    fontWeight: 500,
    marginBottom: theme.spacing(1),
  },
  emptyCartHighlight: {
    color: (props) =>
      props.isDarkMode ? `${props.primaryColor}` : `${props.primaryColor}`,
    fontWeight: 600,
  },
  emptyCartSubtext: {
    color: (props) => (props.isDarkMode ? "#ffffffb5" : "#666"),
    fontSize: "14px",
    textAlign: "center",
    maxWidth: "90%",
    lineHeight: 1.5,
  },
  emptyCartAction: {
    color: "white",
    backgroundColor: (props) => props.primaryColor,
    fontWeight: 600,
    padding: theme.spacing(1, 3),
    borderRadius: "50px",
    textTransform: "none",
    fontSize: "15px",
    boxShadow: (props) => `0 4px 12px ${fade(props.primaryColor, 0.25)}`,
    transition: "transform 0.2s, box-shadow 0.2s",
    fontFamily: "'Inter', 'Helvetica', 'Arial', sans-serif",
    "&:hover": {
      backgroundColor: (props) => darken(props.primaryColor, 0.1),
      transform: "translateY(-2px)",
      boxShadow: (props) => `0 6px 16px ${fade(props.primaryColor, 0.3)}`,
    },
  },

  // Form step styles
  formTitle: {
    marginBottom: theme.spacing(3),
    fontSize: "24px",
    fontWeight: 600,
    position: "relative",
    fontFamily: "'Inter', 'Helvetica', 'Arial', sans-serif",
    color: (props) => (props.isDarkMode ? "#FFFFFF" : "inherit"),
    "&:after": {
      content: '""',
      position: "absolute",
      bottom: "-8px",
      left: "0",
      width: "40px",
      height: "3px",
      backgroundColor: (props) =>
        props.isDarkMode ? `${props.primaryColor}` : `${props.primaryColor}`,
    },
  },
  formPaper: {
    padding: theme.spacing(3),
    borderRadius: "12px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    background: "#ffffff",
    backgroundImage: (props) =>
      `radial-gradient(circle at 90% 10%, ${fade(
        props.primaryColor,
        0.03
      )} 0%, rgba(255, 255, 255, 0) 70%)`,
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
  },
  formField: {
    marginBottom: theme.spacing(1.5),
    "& .MuiOutlinedInput-root": {
      borderRadius: "0px",
      backgroundColor: (props) =>
        props.isDarkMode ? "#000000" : "transparent",
      "& fieldset": {
        borderColor: (props) =>
          props.isDarkMode ? "transparent" : "rgba(0, 0, 0, 0.23)",
        borderWidth: (props) => (props.isDarkMode ? "0" : "1px"),
      },
      "&:hover fieldset": {
        borderColor: (props) =>
          props.isDarkMode
            ? "fade(props.primaryColor, 0.5)"
            : "rgba(0, 0, 0, 0.87)",
        borderWidth: (props) => (props.isDarkMode ? "1px" : "1px"),
      },
      "&.Mui-focused fieldset": {
        borderColor: (props) =>
          props.isDarkMode ? `${props.primaryColor}` : `${props.primaryColor}`,
        borderWidth: (props) => (props.isDarkMode ? "1px" : "1px"),
      },
    },
    "& .MuiInputLabel-root": {
      fontWeight: 500,
      color: (props) => (props.isDarkMode ? "#ffffffb5" : "rgba(0, 0, 0, 0.6)"),
    },
    "& .MuiOutlinedInput-input": {
      padding: "15px 14px",
      color: (props) => (props.isDarkMode ? "#FFFFFF" : "inherit"),
      backgroundColor: (props) =>
        props.isDarkMode ? "#000000" : "transparent",
      "&::placeholder": {
        color: (props) =>
          props.isDarkMode ? "#ffffffb5" : "rgba(0, 0, 0, 0.6)",
        opacity: 1,
      },
      [theme.breakpoints.up("md")]: {
        padding: "12px 14px",
      },
    },
    "&.MuiTextField-root": {
      padding: "0px 4px",
      backgroundColor: (props) => (props.isDarkMode ? "#000000" : "#FFFFFF"),
      borderRadius: "4px",
      border: (props) => `0.5px solid ${props.primaryColor}`,
    },
    [theme.breakpoints.up("md")]: {
      marginBottom: "12px",
    },
  },
  formFieldLabel: {
    marginBottom: "6px",
    fontWeight: 500,
    fontSize: "14px",
    color: (props) => (props.isDarkMode ? "#fff" : "#333"),
    [theme.breakpoints.up("md")]: {
      marginBottom: theme.spacing(0.5),
      fontSize: "14px",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "13px",
    },
  },
  autocompleteInput: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "0px",
      border: (props) =>
        props.isDarkMode
          ? "0.5px solid fade(props.primaryColor, 0.3)"
          : "0.5px solid #e0e0e0",
      backgroundColor: (props) =>
        props.isDarkMode ? "rgb(10, 10, 10, 1)" : "#FFFFFF",
    },
    "& .MuiAutocomplete-inputRoot": {
      padding: "0px !important",
    },
    "& .MuiAutocomplete-input": {
      padding: "12px 14px !important",
      color: (props) => (props.isDarkMode ? "#FFFFFF" : "inherit"),
      backgroundColor: (props) =>
        props.isDarkMode ? "transparent" : "transparent",
      "&::placeholder": {
        color: (props) =>
          props.isDarkMode ? "#ffffffb5" : "rgba(0, 0, 0, 0.6)",
        opacity: 1,
      },
    },
    "& .MuiAutocomplete-popper": {
      maxHeight: "35vh",
      "& .MuiPaper-root": {
        backgroundColor: (props) =>
          props.isDarkMode ? "rgb(10, 10, 10, 1)" : "#FFFFFF",
        color: (props) => (props.isDarkMode ? "#FFFFFF" : "inherit"),
      },
    },
    "& .MuiTextField-root": {
      backgroundColor: (props) =>
        props.isDarkMode ? "rgb(10, 10, 10, 1)" : "#FFFFFF",
      padding: "0px",
    },
  },
  formControl: {
    width: "100%",
    marginBottom: theme.spacing(3),
    "& .MuiInputLabel-root": {
      fontWeight: 500,
    },
    "& .MuiSelect-outlined": {
      padding: "15px 14px",
    },
  },
  formRow: {
    display: "flex",
    flexWrap: "wrap",
    margin: theme.spacing(0, -1),
    "& > *": {
      padding: theme.spacing(0, 1),
      marginBottom: theme.spacing(3),
      [theme.breakpoints.up("sm")]: {
        flex: "0 0 50%",
      },
      [theme.breakpoints.down("xs")]: {
        flex: "0 0 100%",
      },
    },
  },
  submitButton: {
    padding: theme.spacing(1.5),
    backgroundColor: (props) => props.primaryColor,
    color: "#ffffff",
    "&:hover": {
      backgroundColor: (props) => darken(props.primaryColor, 0.1),
    },
    borderRadius: "4px",
    fontWeight: 500,
    fontSize: "16px",
    textTransform: "none",
    marginTop: theme.spacing(1),
    height: "48px",
  },
  backButton: {
    padding: theme.spacing(1.5),
    backgroundColor: "#f5f5f5",
    color: "#000000",
    borderRadius: "4px",
    fontWeight: 500,
    fontSize: "16px",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#e0e0e0",
    },
    marginRight: theme.spacing(2),
    height: "48px",
  },
  buttonGroup: {
    display: "flex",
    marginTop: theme.spacing(2),
  },
  loadingContainer: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: (props) => (props.isDarkMode ? "#000000" : "#ffffff"),
    zIndex: 10001,
    animation: "$fadeIn 0.3s ease-in",
  },
  "@keyframes fadeIn": {
    from: {
      opacity: 0,
    },
    to: {
      opacity: 1,
    },
  },
  "@keyframes pulse": {
    "0%": {
      transform: "scale(1)",
      opacity: 1,
    },
    "50%": {
      transform: "scale(1.05)",
      opacity: 0.8,
    },
    "100%": {
      transform: "scale(1)",
      opacity: 1,
    },
  },
  errorSnackbar: {
    backgroundColor: "#ffebee",
    color: "#d32f2f",
  },
  // Add shake animation for error fields
  "@keyframes shake": {
    "0%, 100%": { transform: "translateX(0)" },
    "10%, 30%, 50%, 70%, 90%": { transform: "translateX(-5px)" },
    "20%, 40%, 60%, 80%": { transform: "translateX(5px)" },
  },
  originalPrice: {
    textDecoration: "line-through",
    color: "#9e9e9e",
    marginRight: theme.spacing(1),
    fontSize: "16px",
  },
  // Mobile-specific styles
  mobileCouponSection: {
    position: "fixed",
    bottom: theme.spacing(7.5),
    left: 0,
    right: 0,
    backgroundColor: (props) => (props.isDarkMode ? "rgb(0, 0, 0)" : "#ffffff"),
    padding: theme.spacing(2),
    borderBottom: (props) =>
      props.isDarkMode
        ? `1px solid ${fade(props.primaryColor, 0.1)}`
        : "1px solid rgba(0, 0, 0, 0.06)",
    zIndex: 9998,
    transition: "all 0.3s ease",
    "&:before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "2px",
      background: (props) =>
        props.isDarkMode
          ? `linear-gradient(to right, ${props.primaryColor}, ${fade(
              props.primaryColor,
              0.3
            )})`
          : `linear-gradient(to right, ${props.primaryColor}, ${fade(
              props.primaryColor,
              0.3
            )})`,
    },
  },
  couponButtonIntegrated: {
    // backgroundColor: (props) =>
    //   props.isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)",
    color: (props) =>
      props.isDarkMode ? `${props.primaryColor}` : `${props.primaryColor}`,
    borderRadius: "4px",
    padding: "8px 20px",
    fontWeight: 600,
    fontSize: "14px",
    textTransform: "none",
    minWidth: "70px",
    height: "36px",
    marginRight: "-8px",
    boxShadow: "none",
    "&:hover": {
      // backgroundColor: (props) =>
      //   props.isDarkMode ? "rgba(255, 255, 255, 0.15)" : "rgba(0, 0, 0, 0.12)",
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      // backgroundColor: (props) =>
      //   props.isDarkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.04)",
      color: (props) =>
        props.isDarkMode
          ? "rgba(255, 255, 255, 0.4)"
          : fade(props.primaryColor, 0.4),
    },
  },
  couponButtonIntegratedApplied: {
    color: (props) =>
      props.isDarkMode ? `${props.primaryColor}` : `${props.primaryColor}`,
    borderRadius: "4px",
    padding: "8px 20px",
    fontWeight: 600,
    fontSize: "14px",
    textTransform: "none",
    minWidth: "70px",
    height: "36px",
    marginRight: "-8px",
    boxShadow: "none",
    "&:hover": {
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      color: (props) =>
        props.isDarkMode
          ? "rgba(255, 255, 255, 0.4)"
          : fade(props.primaryColor, 0.4),
    },
  },
  stickyBottomBar: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1, 2),
    backgroundColor: (props) =>
      props.isDarkMode ? "rgb(10, 10, 10)" : "#ffffff",
    zIndex: 9999,
    height: theme.spacing(7.5),
    maxHeight: "85vh",
    overflowY: "auto",
    animation: "$slideInFromBottom 0.25s ease",
  },
  stickyBottomBarWithLine: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1, 2),
    backgroundColor: (props) => (props.isDarkMode ? "rgb(0, 0, 0)" : "#ffffff"),
    zIndex: 9999,
    height: theme.spacing(7),
    maxHeight: "85vh",
    overflowY: "auto",
    animation: "$slideInFromBottom 0.25s ease",
    "&:before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "2px",
      background: (props) =>
        props.isDarkMode
          ? `linear-gradient(to right, ${props.primaryColor}, ${fade(
              props.primaryColor,
              0.3
            )})`
          : `linear-gradient(to right, ${props.primaryColor}, ${fade(
              props.primaryColor,
              0.3
            )})`,
    },
  },
  stickyCountdownBanner: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: (props) => (props.isDarkMode ? "#000000" : "#ffffff"),
    borderBottom: (props) =>
      props.isDarkMode ? "1px solid #333" : "1px solid #e5e7eb",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    zIndex: 10000,
    padding: theme.spacing(1.5, 2),
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    "&:before": {
      content: '""',
      position: "absolute",
      bottom: 0,
      left: 0,
      width: "100%",
      height: "2px",
      background: (props) =>
        props.isDarkMode
          ? `linear-gradient(to right, ${props.primaryColor}, ${fade(
              props.primaryColor,
              0.3
            )})`
          : `linear-gradient(to right, ${props.primaryColor}, ${fade(
              props.primaryColor,
              0.3
            )})`,
    },
  },
  countdownText: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontSize: "14px",
    fontWeight: 600,
    color: (props) => props.primaryColor,
  },
  mobileTotal: {
    fontWeight: 600,
    fontSize: "16px",
    color: (props) => (props.isDarkMode ? "#FFFFFF" : "inherit"),
  },
  mobileTotalAmount: {
    fontSize: "14px",
    color: (props) => (props.isDarkMode ? "#ffffffb5" : "#666"),
    marginTop: theme.spacing(0.5),
  },
  mobileProceedButton: {
    backgroundColor: (props) => props.primaryColor,
    fontSize: "0.975rem",
    color: (props) => (props.isDarkMode ? "#000" : "#ffffff"),
    "&:hover": {
      backgroundColor: (props) => props.primaryColor,
    },
    fontWeight: 600,
    textTransform: "none",
    "&.Mui-disabled": {
      backgroundColor: "#888888 !important",
      color: "#ffffff !important",
      boxShadow: "none !important",
      opacity: "1 !important",
    },
    padding: theme.spacing(1.2, 3),
    borderRadius: "6px",
    transition: "transform 0.2s, box-shadow 0.2s",
    boxShadow: (props) => `0 2px 8px ${fade(props.primaryColor, 0.3)}`,
    "&:active": {
      transform: "scale(0.98)",
    },
    animation: "$pulseGreen 2s infinite",
    // Ensure loading state is visible
    "&[disabled]": {
      backgroundColor: "#888888 !important",
      color: "#ffffff !important",
      boxShadow: "none !important",
      opacity: "1 !important",
    },
  },
  summaryAccordion: {
    boxShadow: "none",
    backgroundColor: "transparent",
    "&::before": {
      display: "none",
    },
    margin: theme.spacing(2, 0, 3),
    position: "relative",
    "&:after": {
      content: '""',
      position: "absolute",
      bottom: 0,
      left: "10%",
      width: "80%",
      height: "1px",
      background: (props) =>
        `linear-gradient(to right, ${fade(props.primaryColor, 0)}, ${fade(
          props.primaryColor,
          0.3
        )}, ${fade(props.primaryColor, 0)})`,
    },
  },
  summaryAccordionHeader: {
    fontWeight: 600,
    fontSize: "16px",
    color: (props) => props.primaryColor,
    fontFamily: "'Inter', 'Helvetica', 'Arial', sans-serif",
  },
  summaryAccordionContent: {
    padding: 0,
  },
  mobileFormContainer: {
    paddingBottom: theme.spacing(6),
    paddingTop: theme.spacing(1),
  },
  greenText: {
    color: (props) => props.primaryColor,
  },
  dubaiFlair: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "80px",
    background: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 100' preserveAspectRatio='none'%3E%3Cpath fill='%23f0f9f1' d='M0,0 L0,100 L1200,100 L1200,0 L0,0 Z M150,95 L130,95 L130,80 L150,80 L150,95 Z M190,95 L170,95 L170,75 L190,75 L190,95 Z M230,95 L210,95 L210,70 L230,70 L230,95 Z M350,95 L330,95 L330,40 L350,40 L350,95 Z M370,40 L370,95 L390,95 L390,40 L370,40 Z M370,30 L370,35 L380,25 L390,35 L390,30 L380,20 L370,30 Z M520,95 L500,95 L500,60 L520,60 L520,95 Z M540,60 L560,60 L560,95 L540,95 L540,60 Z M700,95 L650,95 L650,50 L700,50 L700,95 Z M725,95 L725,40 L725,30 L700,20 L675,30 L675,40 L675,95 L725,95 Z M800,95 L780,95 L780,60 L800,60 L800,95 Z M880,95 L860,95 L860,85 L880,85 L880,95 Z M900,95 L900,70 L920,70 L920,95 L900,95 Z M1050,95 L1000,95 L1000,30 L1025,5 L1050,30 L1050,95 Z'/%3E%3C/svg%3E") repeat-x bottom center`,
    backgroundSize: "cover",
    zIndex: 0,
    opacity: 0.5,
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  // Update icon styles for custom SVG icons
  iconWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: theme.spacing(1),
    color: "inherit",
  },
  customIcon: {
    color: "inherit",
    verticalAlign: "middle",
    marginRight: theme.spacing(1),
  },
  // Add urgency elements styling
  urgencyBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 14px",
    borderRadius: "20px",
    backgroundColor: "#ffebee",
    color: "#d32f2f",
    fontSize: "15px",
    fontWeight: 600,
    "& svg": {
      marginRight: theme.spacing(0.5),
      fontSize: "16px",
      width: "16px",
      height: "16px",
      fontWeight: 600,
    },
  },

  pulsingDot: {
    height: "10px",
    width: "10px",
    backgroundColor: "#d32f2f",
    borderRadius: "50%",
    display: "inline-block",
    marginRight: "12px",
    animation: "$pulse 1.5s infinite",
  },

  socialProofContainer: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: (props) => fade(props.primaryColor, 0.08),
    borderRadius: "6px",
    border: (props) => `1px dashed ${fade(props.primaryColor, 0.3)}`,
  },

  registrationCounter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: theme.spacing(1, 0),
    "& svg": {
      color: (props) => props.primaryColor,
      marginRight: theme.spacing(1),
    },
  },

  timeLeftContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: theme.spacing(1),
    "& svg": {
      color: "#d32f2f",
      marginRight: theme.spacing(0.5),
    },
  },

  "@keyframes pulse": {
    // "0%": {
    //   transform: "scale(0.95)",
    //   boxShadow: "0 0 0 0 rgba(211, 47, 47, 0.7)",
    // },
    "70%": {
      transform: "scale(1)",
      boxShadow: "0 0 0 8px rgba(211, 47, 47, 0)",
    },
    "100%": {
      transform: "scale(0.95)",
      boxShadow: "0 0 0 0 rgba(211, 47, 47, 0)",
    },
  },

  remainingSpots: {
    backgroundColor: "#FFF8E1",
    color: "#FF9800",
    padding: theme.spacing(0.25, 0.75),
    borderRadius: "4px",
    fontSize: "11px",
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
    marginLeft: theme.spacing(1),
    "& svg": {
      fontSize: "12px",
      marginRight: "2px",
    },
  },

  almostGone: {
    backgroundColor: "#FFEBEE",
    color: "#D32F2F",
  },

  testimonialCard: {
    backgroundColor: "#fff",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    position: "relative",
    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
    "& .MuiAvatar-root": {
      width: 36,
      height: 36,
      border: "2px solid #fff",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    },
  },

  testimonialQuote: {
    fontStyle: "italic",
    fontSize: "14px",
    color: "#555",
    margin: theme.spacing(1, 0),
    paddingLeft: theme.spacing(1),
    borderLeft: (props) => `2px solid ${fade(props.primaryColor, 0.3)}`,
  },

  testimonialAuthor: {
    display: "flex",
    alignItems: "center",
    marginTop: theme.spacing(1),
  },

  testimonialName: {
    marginLeft: theme.spacing(1),
    fontSize: "13px",
    fontWeight: 600,
  },

  testimonialCompany: {
    fontSize: "12px",
    color: "#757575",
  },

  // Enhanced mobile styles
  floatingBadge: {
    position: "fixed",
    top: theme.spacing(2),
    right: theme.spacing(2),
    zIndex: 999,
    backgroundColor: "#d32f2f",
    color: "#fff",
    padding: theme.spacing(0.5, 1.5),
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: 600,
    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [theme.breakpoints.up("md")]: {
      display: "none",
    },
  },

  // Add countdown timer for mobile
  countdownTimer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(0.5),
  },

  countdownUnit: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(0.5),
    minWidth: "36px",
  },

  countdownNumber: {
    backgroundColor: "#1a1a1a",
    color: "#fff",
    padding: theme.spacing(0.5, 1),
    borderRadius: "4px",
    fontWeight: "bold",
    fontSize: "14px",
  },

  countdownLabel: {
    fontSize: "10px",
    marginTop: "2px",
  },
  countryCodeSelector: {
    position: "relative",
    marginRight: theme.spacing(2),
    cursor: "pointer",
    "&:hover": {
      backgroundColor: (props) =>
        props.isDarkMode
          ? "fade(props.primaryColor, 0.1)"
          : (props) => fade(props.primaryColor, 0.08),
    },
  },
  // Add these additional styles for the integrated phone input
  countryCodeInput: {
    display: "flex",
    alignItems: "center",
    backgroundColor: (props) =>
      props.isDarkMode ? "rgb(10, 10, 10, 1)" : "#f9f9f9",
    borderRadius: "4px 0 0 4px",
    padding: "0 8px",
    height: "100%",
    cursor: "pointer",
    borderRight: (props) =>
      props.isDarkMode
        ? "1px solid fade(props.primaryColor, 0.3)"
        : "1px solid #e0e0e0",
    color: (props) => (props.isDarkMode ? "#FFFFFF" : "inherit"),
    "&:hover": {
      backgroundColor: (props) =>
        props.isDarkMode
          ? "fade(props.primaryColor, 0.1)"
          : (props) => fade(props.primaryColor, 0.08),
    },
  },
  phoneInputContainer: {
    display: "flex",
    alignItems: "center",
    "& .MuiOutlinedInput-root": {
      borderRadius: "4px",
      backgroundColor: (props) =>
        props.isDarkMode ? "#000000" : "transparent",
      "& .MuiOutlinedInput-notchedOutline": {
        borderRadius: "4px",
        borderColor: (props) =>
          props.isDarkMode ? "transparent" : "rgba(0, 0, 0, 0.23)",
        borderWidth: (props) => (props.isDarkMode ? "0" : "1px"),
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: (props) =>
          props.isDarkMode
            ? "fade(props.primaryColor, 0.5)"
            : "rgba(0, 0, 0, 0.87)",
        borderWidth: (props) => (props.isDarkMode ? "1px" : "1px"),
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: (props) =>
          props.isDarkMode ? `${props.primaryColor}` : `${props.primaryColor}`,
        borderWidth: (props) => (props.isDarkMode ? "1px" : "1px"),
      },
    },
    "& .MuiOutlinedInput-adornedStart": {
      paddingLeft: "0px",
    },
    "& .MuiOutlinedInput-input": {
      paddingLeft: "8px",
      color: (props) => (props.isDarkMode ? "#FFFFFF" : "inherit"),
      backgroundColor: (props) =>
        props.isDarkMode ? "#000000" : "transparent",
      "&::placeholder": {
        color: (props) =>
          props.isDarkMode ? "#ffffffb5" : "rgba(0, 0, 0, 0.6)",
        opacity: 1,
      },
    },
  },
  mobileMenuPaper: {
    maxHeight: "40vh",
    backgroundColor: (props) =>
      props.isDarkMode ? "rgb(10, 10, 10, 1)" : "#FFFFFF",
    border: (props) =>
      props.isDarkMode
        ? "1px solid fade(props.primaryColor, 0.3)"
        : "1px solid rgba(0, 0, 0, 0.1)",
    "& .MuiMenuItem-root": {
      minHeight: "40px",
      padding: "4px 16px",
      color: (props) => (props.isDarkMode ? "#FFFFFF" : "inherit"),
      backgroundColor: (props) =>
        props.isDarkMode ? "rgb(10, 10, 10, 1)" : "#FFFFFF",
      "&:hover": {
        backgroundColor: (props) =>
          props.isDarkMode
            ? "fade(props.primaryColor, 0.1)"
            : "rgba(0, 0, 0, 0.04)",
      },
    },
    "& .MuiListItem-root": {
      minHeight: "40px",
      padding: "4px 16px",
      color: (props) => (props.isDarkMode ? "#FFFFFF" : "inherit"),
      backgroundColor: (props) =>
        props.isDarkMode ? "rgb(10, 10, 10, 1)" : "#FFFFFF",
      "&:hover": {
        backgroundColor: (props) =>
          props.isDarkMode
            ? "fade(props.primaryColor, 0.1)"
            : "rgba(0, 0, 0, 0.04)",
      },
    },
  },
  mobileAutocompletePaper: {
    maxHeight: "40vh",
    marginTop: "5px",
    marginBottom: "5px",
    backgroundColor: (props) =>
      props.isDarkMode ? "rgb(10, 10, 10, 1)" : "#FFFFFF",
    "& .MuiAutocomplete-option": {
      minHeight: "40px",
      padding: "4px 16px",
      color: (props) => (props.isDarkMode ? "#FFFFFF" : "inherit"),
      "&:hover": {
        backgroundColor: (props) =>
          props.isDarkMode
            ? "fade(props.primaryColor, 0.1)"
            : "rgba(0, 0, 0, 0.04)",
      },
    },
  },
  countrySearchInput: {
    backgroundColor: (props) => (props.isDarkMode ? "#000000" : "#FFFFFF"),
    "& .MuiInput-root": {
      color: (props) => (props.isDarkMode ? "#FFFFFF" : "inherit"),
      "&:before": {
        borderBottom: (props) =>
          props.isDarkMode
            ? "1px solid fade(props.primaryColor, 0.3)"
            : "1px solid rgba(0, 0, 0, 0.42)",
      },
      "&:hover:not(.Mui-disabled):before": {
        borderBottom: (props) =>
          props.isDarkMode
            ? "1px solid fade(props.primaryColor, 0.5)"
            : "1px solid rgba(0, 0, 0, 0.87)",
      },
      "&:after": {
        borderBottom: (props) =>
          props.isDarkMode
            ? `1px solid ${props.primaryColor}`
            : `1px solid ${props.primaryColor}`,
      },
    },
    "& .MuiInput-input": {
      color: (props) => (props.isDarkMode ? "#FFFFFF" : "inherit"),
      backgroundColor: (props) =>
        props.isDarkMode ? "#000000" : "transparent",
      "&::placeholder": {
        color: (props) =>
          props.isDarkMode ? "#ffffffb5" : "rgba(0, 0, 0, 0.6)",
        opacity: 1,
      },
    },
    "& .MuiInputAdornment-root": {
      color: (props) => (props.isDarkMode ? "#ffffffb5" : "rgba(0, 0, 0, 0.6)"),
    },
  },
  countrySelect: {
    "& .MuiSelect-root": {
      color: (props) => (props.isDarkMode ? "#FFFFFF" : "inherit"),
      backgroundColor: (props) =>
        props.isDarkMode ? "transparent" : "transparent",
      "&:focus": {
        backgroundColor: (props) =>
          props.isDarkMode ? "transparent" : "transparent",
      },
    },
    "& .MuiSelect-icon": {
      color: (props) => (props.isDarkMode ? "#FFFFFF" : "inherit"),
    },
    "&:before": {
      borderBottom: (props) =>
        props.isDarkMode ? "none" : "1px solid rgba(0, 0, 0, 0.42)",
    },
    "&:after": {
      borderBottom: (props) =>
        props.isDarkMode ? `none` : `1px solid ${props.primaryColor}`,
    },
    "&:hover:not(.Mui-disabled):before": {
      borderBottom: (props) =>
        props.isDarkMode ? "none" : "1px solid rgba(0, 0, 0, 0.87)",
    },
  },
  radioGroup: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    width: "101%",
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.up("md")]: {
      marginBottom: theme.spacing(2),
    },
  },
  radioButton: {
    flex: "0 0 calc(50% - 5px)",
    margin: 0,
    [theme.breakpoints.down("xs")]: {
      flex: "0 0 100%",
    },
  },
  radioLabel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    width: "92%",
    padding: "8px 12px",
    border: (props) => `0.5px solid ${fade(props.primaryColor, 0.3)}`,
    borderRadius: "4px",
    transition: "all 0.2s ease",
    cursor: "pointer",
    backgroundColor: (props) => (props.isDarkMode ? "#000000" : "#FFFFFF"),
    color: (props) => (props.isDarkMode ? "#FFFFFF" : "inherit"),
    // "&:hover": {
    //   backgroundColor: (props) => (props.isDarkMode ? "#000000" : "#f9f9f9"),
    //   borderColor: (props) =>
    //     props.isDarkMode ? "fade(props.primaryColor, 0.3)" : "#cccccc",
    // },
  },
  radioLabelSelected: {
    backgroundColor: (props) =>
      props.isDarkMode ? "" : fade(props.primaryColor, 0.08),
    borderColor: (props) =>
      props.isDarkMode ? `${props.primaryColor}` : `${props.primaryColor}`,
    "& .MuiFormControlLabel-label": {
      fontWeight: 500,
    },
  },
  radioRoot: {
    padding: "0",
    marginRight: "8px",
    color: (props) =>
      props.isDarkMode ? `${props.primaryColor}` : `${props.primaryColor}`,
  },
  radioErrorText: {
    color: "#f44336",
    fontSize: "12px",
    marginTop: "4px",
    marginLeft: "14px",
  },

  // Enhanced GST Input Section with collapsible fields
  gstInputSection: {
    marginTop: theme.spacing(1),
    // marginBottom: theme.spacing(1),
    // border: "1px solid #e0e0e0",
    // borderRadius: "8px",
    padding: "3px",
    // backgroundColor: "#fafafa",
    transition: "all 0.3s ease",
    "& .MuiTextField-root": {
      padding: "0px",
      backgroundColor: (props) => (props.isDarkMode ? "#2a2a2a" : "#fff"),
    },
  },
  gstInputLabel: {
    marginBottom: theme.spacing(1),
    fontWeight: 600,
    fontSize: "14px",
    color: (props) => (props.isDarkMode ? "#FFFFFF" : "#222"),
    fontFamily: "'Inter', 'Helvetica', 'Arial', sans-serif",
  },
  gstCheckboxContainer: {
    // marginTop: theme.spacing(1),
    // marginBottom: theme.spacing(1),
    "& .MuiFormControlLabel-label": {
      fontSize: "14px",
      fontWeight: 500,
      color: (props) => (props.isDarkMode ? "#FFFFFF" : "#333"),
      fontFamily: "'Inter', 'Helvetica', 'Arial', sans-serif",
    },
    "& .MuiCheckbox-root": {
      color: (props) =>
        props.isDarkMode ? `${props.primaryColor}` : `${props.primaryColor}`,
      "&.Mui-checked": {
        color: (props) =>
          props.isDarkMode ? `${props.primaryColor}` : `${props.primaryColor}`,
      },
    },
  },
  gstDetailsCollapsible: {
    marginTop: theme.spacing(2),
    overflow: "hidden",
    transition: "max-height 0.3s ease, opacity 0.3s ease",
    "&.collapsed": {
      maxHeight: 0,
      opacity: 0,
    },
    "&.expanded": {
      maxHeight: "500px",
      opacity: 1,
    },
  },
  gstFieldContainer: {
    marginBottom: theme.spacing(2),
    "&:last-child": {
      marginBottom: 0,
    },
  },
  gstTextField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "6px",
      backgroundColor: (props) => (props.isDarkMode ? "#000" : "#fff"),
      border: (props) => `1px solid ${props.primaryColor}`,
      transition: "all 0.2s ease",
      "&:hover": {
        borderColor: (props) =>
          props.isDarkMode ? `${props.primaryColor}` : `${props.primaryColor}`,
      },
      "&.Mui-focused": {
        borderColor: (props) =>
          props.isDarkMode ? `${props.primaryColor}` : `${props.primaryColor}`,
        boxShadow: (props) =>
          props.isDarkMode
            ? `0 0 0 2px ${fade(props.primaryColor, 0.1)}`
            : `0 0 0 2px ${fade(props.primaryColor, 0.1)}`,
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "12px 14px",
      // textTransform: "uppercase",
      fontSize: "14px",
      fontWeight: 500,
      letterSpacing: "0.5px",
      color: (props) => (props.isDarkMode ? "#FFFFFF" : "inherit"),
      "&::placeholder": {
        color: (props) => (props.isDarkMode ? "#ffffffb5" : "#999"),
        textTransform: "none",
        letterSpacing: "normal",
        fontWeight: 400,
      },
    },
    "& .MuiFormHelperText-root": {
      fontSize: "12px",
      marginTop: theme.spacing(0.5),
      marginLeft: theme.spacing(0.2),
    },
  },

  // Add a green pulse animation just for the button
  "@keyframes pulseGreen": {
    "0%": {
      transform: "scale(0.95)",
      boxShadow: (props) => `0 0 0 0 ${fade(props.primaryColor, 0.7)}`,
    },
    "70%": {
      transform: "scale(1)",
      boxShadow: (props) => `0 0 0 8px ${fade(props.primaryColor, 0)}`,
    },
    "100%": {
      transform: "scale(0.95)",
      boxShadow: (props) => `0 0 0 0 ${fade(props.primaryColor, 0)}`,
    },
  },

  // Ultra-fast slide-in animation
  "@keyframes slideInFromBottom": {
    "0%": {
      transform: "translateY(30%)", // Reduced distance for faster appearance
      opacity: 0.5, // Start with higher opacity for faster fade-in
    },
    "100%": {
      transform: "translateY(0)",
      opacity: 1,
    },
  },

  // Ultra-fast summary expansion animation
  "@keyframes expandSummary": {
    "0%": {
      maxHeight: "0",
      opacity: 0.5, // Start with higher opacity for faster fade-in
      transform: "translateY(5px)", // Minimal distance for faster appearance
    },
    "100%": {
      maxHeight: "60vh",
      opacity: 1,
      transform: "translateY(0)",
    },
  },

  // Add a disappearing animation keyframe
  "@keyframes collapseSummary": {
    "0%": {
      maxHeight: "60vh",
      opacity: 1,
      transform: "translateY(0)",
    },
    "100%": {
      maxHeight: "0",
      opacity: 0.5,
      transform: "translateY(5px)",
    },
  },
}));

export default AiSensyPaymentPage;
